import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  FileUp,
  History,
  KeyRound,
  Languages,
  LockKeyhole,
  LogIn,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  MessageSquare,
  Pencil,
  Phone,
  Plug,
  Plus,
  RotateCcw,
  Search,
  Send,
  Settings,
  ShieldCheck,
  StickyNote,
  Target,
  Trash2,
  Upload,
  UserPlus,
  Users,
  Workflow,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

import {
  api,
  apiBaseUrl,
  ApiError,
  uploadAvatar,
  type Appointment,
  type AppointmentType,
  type Applicant,
  type ApplicantList,
  type Application,
  type CalcomConnection,
  type ClickSendConnection,
  type DocumentRequest,
  type Notice,
  type NotificationTemplate,
  type OutlookConnection,
  type SchedulingHandoff,
  type Session,
  type SharedResource,
  type StaffApplicationHistory,
  type StaffUser,
  type StageChecklist,
  type StageStatusValue,
  type StudyMaterial,
  type WorkflowStage,
} from '../lib/api';
import { ApplicantPortal } from '../portal/ApplicantPortal';
import { Avatar } from '../shared/Avatar';
import { CalcomEmbed } from '../shared/CalcomEmbed';
import { ChangePasswordForm, ChangePasswordModal } from '../shared/ChangePasswordModal';
import { InfoTip } from '../shared/InfoTip';
import { ActionMenu } from '../shared/ActionMenu';
import { SearchableSelect } from '../shared/SearchableSelect';
import { StatusBadge } from '../shared/StatusBadge';
import {
  applicationLifecycleTone,
  appointmentStatusTone,
  documentRequestStatusTone,
  integrationStatusTone,
  stageStatusTone,
  staffStatusTone,
  templateActiveTone,
  versionPublishedTone,
} from '../shared/statusTones';

type Stage = WorkflowStage;

/**
 * The single place that turns a caught error into text a non-technical user can act on.
 * Backend AppError messages are already written in plain language (see backend route zod
 * schemas and AppError call sites), so this mostly just passes error.message through -- its
 * real job is filling the two gaps the backend can't: appending which specific field(s) failed
 * validation when the server flagged more than one, and giving network-level failures (caught
 * in lib/api.ts as ApiError code 'NETWORK_ERROR') their own message instead of a generic one.
 */
function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fieldDetail = Array.from(
      new Set((error.fieldErrors ?? []).map((field) => field.message.trim()).filter(Boolean)),
    ).filter((detail) => !error.message.includes(detail));
    return fieldDetail.length ? `${error.message} ${fieldDetail.join(' ')}` : error.message;
  }
  return "We couldn't complete that action. Please try again.";
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0]![0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]![0]! : '';
  return (first + last).toUpperCase();
}

// Reason codes set by the backend's OAuth callback redirect (see
// backend/src/routes/outlook-oauth.ts's `fail()`), carried back to the browser as a query
// param rather than a normal API error response. Mapped here to plain language instead of
// showing the raw code, with a safe fallback for any code this list doesn't recognize.
const OUTLOOK_CONNECTION_ERRORS: Record<string, string> = {
  access_denied: 'Microsoft sign-in was cancelled. Try connecting again.',
  state_mismatch: 'That connection attempt expired or could not be verified. Try connecting again.',
  not_configured:
    'Outlook is not fully set up yet. Check the Tenant ID, Client ID, and Client secret, then try again.',
  token_exchange_failed: "Microsoft couldn't confirm the connection. Please try again.",
  no_refresh_token:
    'Microsoft did not grant the access this app needs. Try connecting again and approve all requested permissions.',
  profile_fetch_failed: "We couldn't retrieve the mailbox details from Microsoft. Please try again.",
};
function outlookConnectionErrorMessage(reason: string): string {
  return OUTLOOK_CONNECTION_ERRORS[reason] ?? 'Outlook connection failed. Please try again.';
}

const PASSWORD_REQUIREMENT_HINT = 'Use 6–128 characters.';

const DOCUMENT_REQUEST_STATUS_LABELS: Record<string, string> = {
  pending: 'In Progress',
  uploaded: 'Needs Review',
  under_review: 'Needs Review',
  accepted: 'Approved',
  waived: 'Waived',
  replacement_requested: 'In Revision',
  rejected: 'In Revision',
  cancelled: 'Cancelled',
  expired: 'Expired',
};
const documentRequestStatusLabel = (status: string) =>
  DOCUMENT_REQUEST_STATUS_LABELS[status] ?? status.replaceAll('_', ' ');
// Documents staff can open: 'quarantined' is included because nothing in this codebase
// clears quarantine automatically yet, and staff still need to review the file itself.
const DOWNLOADABLE_DOCUMENT_STATUSES = new Set(['quarantined', 'available', 'accepted']);

const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  pending_confirmation: 'Pending confirmation',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
  rejected: 'Not confirmed',
  completed: 'Completed',
  no_show: 'Missed',
};
const appointmentStatusLabel = (status: string) =>
  APPOINTMENT_STATUS_LABELS[status] ?? status.replaceAll('_', ' ');

export function App() {
  const queryClient = useQueryClient();
  // A password-reset link points here with ?resetToken=... (see auth/password-reset/request
  // on the backend); that flow works purely off the token and never needs a session.
  const resetToken = new URLSearchParams(window.location.search).get('resetToken');
  // An invitation email (applicant or staff) points here with ?inviteToken=...; also works
  // purely off the token, before any session exists.
  const inviteToken = new URLSearchParams(window.location.search).get('inviteToken');
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api<Session>('/auth/session'),
    retry: false,
    staleTime: 60_000,
    enabled: !resetToken && !inviteToken,
  });

  if (resetToken) return <ResetPasswordScreen token={resetToken} />;
  if (inviteToken) return <AcceptInvitationScreen token={inviteToken} />;
  if (session.isLoading) {
    return <AppLoading />;
  }
  if (session.error instanceof ApiError && session.error.code === 'UNAUTHORIZED') {
    return (
      <LoginScreen
        onAuthenticated={(authenticated) => queryClient.setQueryData(['session'], authenticated)}
      />
    );
  }
  if (session.isError || !session.data) {
    return (
      <main className="grid min-h-screen place-items-center bg-canvas p-4 text-ink">
        <section className="schd-card max-w-md text-center" role="alert">
          <ShieldCheck className="mx-auto size-8 text-burgundy" aria-hidden="true" />
          <h1 className="mt-4 font-display text-2xl">Session unavailable</h1>
          <p className="mt-3 text-ink/70">
            Your secure session could not be verified. Refresh the page or sign in again.
          </p>
          <button className="schd-button-primary mt-5" onClick={() => window.location.reload()}>
            Try again
          </button>
        </section>
      </main>
    );
  }
  if (session.data.user.role === 'applicant') return <ApplicantPortal session={session.data} />;
  return <StaffWorkspace isAdmin={session.data?.user.role === 'admin'} />;
}

function LoginScreen({ onAuthenticated }: { onAuthenticated: (session: Session) => void }) {
  const [mode, setMode] = useState<'login' | 'forgot' | 'forgot-sent'>('login');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (error) document.getElementById('login-error')?.focus();
  }, [error]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    setError('');
    try {
      const session = await api<Session>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: form.get('email'), password: form.get('password') }),
      });
      onAuthenticated(session);
    } catch (loginError) {
      setError(errorMessage(loginError));
    } finally {
      setSubmitting(false);
    }
  };
  const submitForgot = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    setError('');
    try {
      await api('/auth/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({ email: form.get('email') }),
      });
      setMode('forgot-sent');
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main className="grid min-h-screen bg-canvas text-ink lg:grid-cols-[minmax(320px,.8fr)_minmax(480px,1.2fr)]">
      <section className="flex items-center bg-burgundy-dark p-6 text-white sm:p-10 lg:p-14">
        <div className="mx-auto w-full max-w-lg lg:mx-0">
          <img
            src="/brand/schd-logo-white.png"
            alt="South Central Hispanic District"
            className="h-auto w-64 max-w-full"
          />
          <p className="mt-8 text-sm font-semibold uppercase tracking-[.12em] text-white/70">
            Credential management
          </p>
          <h1 className="mt-3 font-display text-3xl leading-tight sm:text-4xl">
            A clear path through credentialing.
          </h1>
          <p className="mt-4 max-w-md leading-7 text-white/75">
            Secure access for applicants, staff, and administrators of the South Central Hispanic
            District.
          </p>
        </div>
      </section>
      <section className="flex items-center p-4 sm:p-8 lg:p-14">
        {mode === 'login' && (
          <form className="schd-card mx-auto w-full max-w-md" onSubmit={submit}>
            <p className="schd-eyebrow">Secure portal</p>
            <h2 className="mt-2 font-display text-2xl">Sign in</h2>
            <p className="mt-2 text-sm text-ink/65">
              Use the email address associated with your SCHD portal account.
            </p>
            {error && (
              <div id="login-error" className="schd-error mt-5" role="alert" tabIndex={-1}>
                {error}
              </div>
            )}
            <label className="mt-5 block font-semibold" htmlFor="login-email">
              Email address (required)
            </label>
            <div className="relative mt-1">
              <Mail
                className="pointer-events-none absolute left-3 top-3 size-5 text-ink/50"
                aria-hidden="true"
              />
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                disabled={submitting}
                className="min-h-11 w-full rounded-control border pl-10 pr-3"
              />
            </div>
            <label className="mt-4 block font-semibold" htmlFor="login-password">
              Password (required)
            </label>
            <div className="relative mt-1">
              <LockKeyhole
                className="pointer-events-none absolute left-3 top-3 size-5 text-ink/50"
                aria-hidden="true"
              />
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                maxLength={128}
                disabled={submitting}
                className="min-h-11 w-full rounded-control border pl-10 pr-3"
              />
            </div>
            <button className="schd-button-primary mt-6 w-full" disabled={submitting}>
              <LogIn className="size-5" aria-hidden="true" />
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
            <p className="mt-5 text-center text-sm text-ink/60">
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setError('');
                  setMode('forgot');
                }}
              >
                Forgot your password?
              </button>{' '}
              · Need portal access? Contact SCHD credentialing staff.
            </p>
          </form>
        )}
        {mode === 'forgot' && (
          <form className="schd-card mx-auto w-full max-w-md" onSubmit={submitForgot}>
            <p className="schd-eyebrow">Secure portal</p>
            <h2 className="mt-2 font-display text-2xl">Reset your password</h2>
            <p className="mt-2 text-sm text-ink/65">
              Enter the email address on your account. If it matches an active account, we will
              send password reset instructions.
            </p>
            {error && (
              <div id="login-error" className="schd-error mt-5" role="alert" tabIndex={-1}>
                {error}
              </div>
            )}
            <label className="mt-5 block font-semibold" htmlFor="forgot-email">
              Email address (required)
            </label>
            <div className="relative mt-1">
              <Mail
                className="pointer-events-none absolute left-3 top-3 size-5 text-ink/50"
                aria-hidden="true"
              />
              <input
                id="forgot-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                disabled={submitting}
                className="min-h-11 w-full rounded-control border pl-10 pr-3"
              />
            </div>
            <button className="schd-button-primary mt-6 w-full" disabled={submitting}>
              <Send className="size-5" aria-hidden="true" />
              {submitting ? 'Sending…' : 'Send reset instructions'}
            </button>
            <button
              type="button"
              className="mt-5 block w-full text-center text-sm underline"
              onClick={() => {
                setError('');
                setMode('login');
              }}
            >
              Back to sign in
            </button>
          </form>
        )}
        {mode === 'forgot-sent' && (
          <section className="schd-card mx-auto w-full max-w-md text-center">
            <ShieldCheck className="mx-auto size-8 text-burgundy" aria-hidden="true" />
            <h2 className="mt-4 font-display text-2xl">Check your email</h2>
            <p className="mt-3 text-ink/70">
              If that email matches an active account, password reset instructions are on the way.
              The link expires after a short time.
            </p>
            <button
              className="schd-button-primary mt-6"
              onClick={() => {
                setError('');
                setMode('login');
              }}
            >
              Back to sign in
            </button>
          </section>
        )}
      </section>
    </main>
  );
}

function ResetPasswordScreen({ token }: { token: string }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get('password') ?? '');
    const confirm = String(form.get('confirm') ?? '');
    if (password !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api('/auth/password-reset/complete', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setDone(true);
    } catch (resetError) {
      setError(errorMessage(resetError));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main className="grid min-h-screen place-items-center bg-canvas p-4 text-ink">
      {done ? (
        <section className="schd-card max-w-md text-center">
          <ShieldCheck className="mx-auto size-8 text-burgundy" aria-hidden="true" />
          <h1 className="mt-4 font-display text-2xl">Password updated</h1>
          <p className="mt-3 text-ink/70">You can now sign in with your new password.</p>
          <button
            className="schd-button-primary mt-5"
            onClick={() => {
              window.location.href = window.location.pathname;
            }}
          >
            Continue to sign in
          </button>
        </section>
      ) : (
        <form className="schd-card mx-auto w-full max-w-md" onSubmit={submit}>
          <p className="schd-eyebrow">Secure portal</p>
          <h2 className="mt-2 font-display text-2xl">Choose a new password</h2>
          <p className="mt-2 text-sm text-ink/65">
            This reset link is single-use and expires after a short time.
          </p>
          {error && (
            <div className="schd-error mt-5" role="alert">
              {error}
            </div>
          )}
          <label className="mt-5 block font-semibold" htmlFor="reset-password">
            New password (required)
          </label>
          <div className="relative mt-1">
            <LockKeyhole
              className="pointer-events-none absolute left-3 top-3 size-5 text-ink/50"
              aria-hidden="true"
            />
            <input
              id="reset-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              maxLength={128}
              disabled={submitting}
              className="min-h-11 w-full rounded-control border pl-10 pr-3"
            />
          </div>
          <p className="mt-1 text-xs text-ink/60">{PASSWORD_REQUIREMENT_HINT}</p>
          <label className="mt-4 block font-semibold" htmlFor="reset-confirm">
            Confirm new password (required)
          </label>
          <div className="relative mt-1">
            <LockKeyhole
              className="pointer-events-none absolute left-3 top-3 size-5 text-ink/50"
              aria-hidden="true"
            />
            <input
              id="reset-confirm"
              name="confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              maxLength={128}
              disabled={submitting}
              className="min-h-11 w-full rounded-control border pl-10 pr-3"
            />
          </div>
          <button className="schd-button-primary mt-6 w-full" disabled={submitting}>
            {submitting ? 'Saving…' : 'Set new password'}
          </button>
        </form>
      )}
    </main>
  );
}

function AcceptInvitationScreen({ token }: { token: string }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get('password') ?? '');
    const confirm = String(form.get('confirm') ?? '');
    if (password !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api('/auth/invitations/accept', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      window.location.href = window.location.pathname;
    } catch (acceptError) {
      setError(errorMessage(acceptError));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main className="grid min-h-screen place-items-center bg-canvas p-4 text-ink">
      <form className="schd-card mx-auto w-full max-w-md" onSubmit={submit}>
        <p className="schd-eyebrow">Secure portal</p>
        <h2 className="mt-2 font-display text-2xl">Set your password</h2>
        <p className="mt-2 text-sm text-ink/65">
          Choose a password to activate your account. This invitation link is single-use and
          expires after a short time.
        </p>
        {error && (
          <div className="schd-error mt-5" role="alert">
            {error}
          </div>
        )}
        <label className="mt-5 block font-semibold" htmlFor="invite-password">
          New password (required)
        </label>
        <div className="relative mt-1">
          <LockKeyhole
            className="pointer-events-none absolute left-3 top-3 size-5 text-ink/50"
            aria-hidden="true"
          />
          <input
            id="invite-password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={6}
            maxLength={128}
            disabled={submitting}
            className="min-h-11 w-full rounded-control border pl-10 pr-3"
          />
        </div>
        <p className="mt-1 text-xs text-ink/60">{PASSWORD_REQUIREMENT_HINT}</p>
        <label className="mt-4 block font-semibold" htmlFor="invite-confirm">
          Confirm new password (required)
        </label>
        <div className="relative mt-1">
          <LockKeyhole
            className="pointer-events-none absolute left-3 top-3 size-5 text-ink/50"
            aria-hidden="true"
          />
          <input
            id="invite-confirm"
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={6}
            maxLength={128}
            disabled={submitting}
            className="min-h-11 w-full rounded-control border pl-10 pr-3"
          />
        </div>
        <button className="schd-button-primary mt-6 w-full" disabled={submitting}>
          {submitting ? 'Setting up your account…' : 'Activate account'}
        </button>
      </form>
    </main>
  );
}

function AppLoading() {
  return (
    <main
      className="min-h-screen bg-canvas p-4 sm:p-6"
      aria-live="polite"
      aria-label="Loading workspace"
    >
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="schd-skeleton h-16 w-full" />
        <div className="schd-skeleton h-32 w-full" />
        <div className="schd-skeleton h-96 w-full" />
      </div>
    </main>
  );
}

function useDialogFocus(onClose: () => void) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      const isTopDialog = dialogs[dialogs.length - 1] === dialogRef.current;
      if (event.key === 'Escape' && isTopDialog) onClose();
      if (!isTopDialog) return;
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);
  return dialogRef;
}

// Delays a dialog's actual unmount by ~120ms so its exit animation (schd-modal-*-out) has time
// to play -- otherwise React removes the panel the instant a close handler fires and the fade/
// scale-in it opened with has no closing counterpart.
function useModalClosing(onClose: () => void, durationMs = 120) {
  const [closing, setClosing] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timeoutRef.current), []);
  const requestClose = useCallback(() => {
    if (timeoutRef.current) return;
    setClosing(true);
    timeoutRef.current = setTimeout(onClose, durationMs);
  }, [onClose, durationMs]);
  return { closing, requestClose };
}

function StageProgress({
  stages,
  currentSortOrder,
}: {
  stages: WorkflowStage[];
  currentSortOrder?: number;
}) {
  if (!stages.length || currentSortOrder === undefined) return null;
  const position = stages.filter((stage) => stage.sortOrder <= currentSortOrder).length;
  return (
    <div
      className="mt-1.5 flex items-center gap-1.5"
      role="img"
      aria-label={`Stage ${position} of ${stages.length}`}
    >
      {stages.map((stage) => (
        <span
          key={stage.id}
          aria-hidden="true"
          className={
            stage.sortOrder <= currentSortOrder
              ? 'size-2 rounded-full bg-burgundy'
              : 'size-2 rounded-full border border-ink/30'
          }
        />
      ))}
      <span className="ml-0.5 text-[11px] font-medium text-ink/50">
        {position}/{stages.length}
      </span>
    </div>
  );
}

function StaffWorkspace({ isAdmin }: { isAdmin: boolean }) {
  const [mobileNav, setMobileNav] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('-updatedAt');
  const [stageId, setStageId] = useState('');
  const [view, setView] = useState<
    'applicants' | 'processed' | 'archived' | 'materials' | 'settings'
  >('applicants');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const queryClient = useQueryClient();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('outlookConnected');
    const error = params.get('outlookError');
    if (!connected && !error) return;
    if (connected) toast.success('Microsoft Outlook connected.');
    if (error) toast.error(outlookConnectionErrorMessage(error));
    setView('settings');
    window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const archived = view === 'archived';
  const processed = view === 'processed';
  const filtersActive = debouncedSearch.trim() !== '' || stageId !== '';
  const applicants = useQuery({
    queryKey: ['applicants', debouncedSearch, page, sort, stageId, archived, processed],
    queryFn: () =>
      api<ApplicantList>(
        `/applicants?page=${page}&pageSize=15&sort=${sort}&archived=${archived}&processed=${processed}${debouncedSearch ? `&q=${encodeURIComponent(debouncedSearch)}` : ''}${stageId ? `&workflowStageId=${stageId}` : ''}`,
      ),
    enabled: view === 'applicants' || view === 'archived' || view === 'processed',
  });
  const stages = useQuery({
    queryKey: ['workflow-stages'],
    queryFn: () => api<{ data: WorkflowStage[] }>('/settings/workflow-stages'),
    staleTime: 5 * 60_000,
  });
  const detail = useQuery({
    queryKey: ['applicant', selectedId],
    queryFn: () => api<Applicant>(`/applicants/${selectedId}`),
    enabled: Boolean(selectedId),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['applicants'] });
    if (selectedId) void queryClient.invalidateQueries({ queryKey: ['applicant', selectedId] });
  };
  const logout = async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
      window.location.reload();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <main className="min-h-screen bg-canvas text-ink md:flex">
      <a
        href="#applicants"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-control focus:bg-burgundy focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to main content
      </a>
      <aside
        id="staff-navigation"
        className={`schd-sidebar flex-col border-b border-border bg-burgundy-dark text-white md:sticky md:top-0 md:h-screen md:w-64 md:self-start md:overflow-y-auto md:border-b-0 md:border-r ${mobileNav ? 'schd-drawer-panel-left flex' : 'hidden md:flex'}`}
      >
        <div className="p-6">
          <img
            src="/brand/schd-logo-white.png"
            alt="South Central Hispanic District"
            className="h-auto w-40 max-w-full"
          />
          <p className="mt-2 text-sm text-white/70">Credential Application Tracking</p>
        </div>
        <nav
          className="flex flex-col gap-1 border-t border-white/15 px-3 py-4"
          aria-label="Staff navigation"
        >
          {(
            [
              ['applicants', 'Applicants', Users],
              ['processed', 'Processed', CheckCircle2],
              ['archived', 'Archived', Archive],
              ['materials', 'Shared Resources', BookOpen],
            ] as const
          ).map(([id, label, Icon]) => (
            <button
              key={id}
              className={`flex min-h-11 w-full items-center gap-3 rounded-control border-l-[3px] pl-3 pr-3 text-left text-[0.95rem] ${
                view === id
                  ? 'border-l-gold bg-white/15 font-semibold text-white'
                  : 'border-l-transparent text-white/85 hover:bg-white/10 hover:text-white'
              }`}
              aria-current={view === id ? 'page' : undefined}
              onClick={() => {
                setView(id);
                setSelectedId(null);
                setPage(1);
                setMobileNav(false);
              }}
            >
              <Icon className="size-5 shrink-0" aria-hidden="true" /> {label}
            </button>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-1 border-t border-white/15 px-4 py-4">
          {isAdmin && (
            <button
              className={`-mx-2 flex min-h-11 items-center gap-2 rounded-control px-2 text-left text-sm ${
                view === 'settings'
                  ? 'font-semibold text-white'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
              aria-current={view === 'settings' ? 'page' : undefined}
              onClick={() => {
                setView('settings');
                setMobileNav(false);
              }}
            >
              <Settings className="size-4 shrink-0" aria-hidden="true" />
              Settings
            </button>
          )}
          {!isAdmin && (
            <button
              onClick={() => setChangePasswordOpen(true)}
              className="-mx-2 flex min-h-11 items-center gap-2 rounded-control px-2 text-sm text-white/80 hover:bg-white/10 hover:text-white"
            >
              <KeyRound className="size-4 shrink-0" aria-hidden="true" />
              Change password
            </button>
          )}
          <button
            onClick={() => void logout()}
            className="-mx-2 flex min-h-11 items-center gap-2 rounded-control px-2 text-sm text-white/80 hover:bg-white/10 hover:text-white"
          >
            <LogOut className="size-4 shrink-0" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </aside>
      <section className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8" id="applicants">
        <button
          className="mb-4 grid size-11 place-items-center rounded-control border border-border bg-surface md:hidden"
          aria-label="Toggle navigation"
          aria-expanded={mobileNav}
          aria-controls="staff-navigation"
          onClick={() => setMobileNav(!mobileNav)}
        >
          <Menu />
        </button>
        {view === 'settings' ? (
          <SettingsPanel />
        ) : view === 'materials' ? (
          <StudyMaterialsPanel />
        ) : selectedId ? (
          <ApplicantPanel
            applicant={detail.data}
            loading={detail.isLoading}
            error={detail.error}
            isAdmin={isAdmin}
            onClose={() => setSelectedId(null)}
            onRefresh={refresh}
          />
        ) : (
          <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              {archived ? (
                <Archive className="size-5 text-burgundy" aria-hidden="true" />
              ) : processed ? (
                <CheckCircle2 className="size-5 text-burgundy" aria-hidden="true" />
              ) : (
                <Users className="size-5 text-burgundy" aria-hidden="true" />
              )}
              <h1 className="font-display text-2xl sm:text-3xl">
                {archived ? 'Archived Applicants' : processed ? 'Processed' : 'Applicants'}
              </h1>
              <InfoTip
                label={
                  archived
                    ? 'About archived applicants'
                    : processed
                      ? 'About processed applications'
                      : 'About applicants'
                }
              >
                {archived
                  ? 'These applicants are no longer part of active workflows. Open a record to restore it.'
                  : processed
                    ? 'Applications that have completed every stage of the workflow. They move here automatically and no longer appear under Applicants.'
                    : 'Every applicant in your active pipeline, across all stages. Search, filter, or open a record to review details.'}
              </InfoTip>
            </div>
          </div>
          {!archived && !processed && (
            <button className="schd-button-primary" onClick={() => setShowCreate(true)}>
              <Plus className="size-5" /> New applicant
            </button>
          )}
        </div>
        <div className="mb-5 grid gap-3 rounded-card border border-border bg-surface p-4 shadow-card md:grid-cols-[1fr_260px_260px]">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold">Search applicants</span>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-ink/55"
                aria-hidden="true"
              />
              <input
                className="min-h-11 w-full rounded-control border bg-white pl-10 pr-3"
                placeholder="Search name or email"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </label>
          <label className="text-sm">
            Stage
            <div className="mt-1">
              <SearchableSelect
                value={stageId}
                onChange={(next) => {
                  setStageId(next);
                  setPage(1);
                }}
                placeholder="All stages"
                options={[
                  { value: '', label: 'All stages' },
                  ...(stages.data?.data.map((stage) => ({
                    value: stage.id,
                    label: stage.translations.en.name,
                  })) ?? []),
                ]}
              />
            </div>
          </label>
          <label className="text-sm">
            Sort
            <div className="mt-1">
              <SearchableSelect
                value={sort}
                onChange={(next) => {
                  setSort(next);
                  setPage(1);
                }}
                options={[
                  { value: '-updatedAt', label: 'Recently updated' },
                  { value: 'lastName', label: 'Last name' },
                  { value: 'stage', label: 'Stage' },
                ]}
              />
            </div>
          </label>
        </div>
        {applicants.isLoading ? (
          <div className="rounded-card border border-border bg-surface p-6" role="status">
            <span className="sr-only">Loading applicants</span>
            <div className="space-y-3" aria-hidden="true">
              <div className="schd-skeleton h-5 w-2/5" />
              <div className="schd-skeleton h-12 w-full" />
              <div className="schd-skeleton h-12 w-full" />
            </div>
          </div>
        ) : applicants.isError ? (
          <div className="schd-error" role="alert">
            {errorMessage(applicants.error)}{' '}
            <button className="ml-2 underline" onClick={() => applicants.refetch()}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-card border border-border bg-surface">
              <div className="hidden grid-cols-[1.4fr_1.3fr_1fr_1fr_20px] gap-4 border-b p-4 text-sm font-semibold text-ink/65 md:grid">
                <span>Applicant</span>
                <span>Stage</span>
                <span>Assigned</span>
                <span>Updated</span>
                <span aria-hidden="true" />
              </div>
              {applicants.data?.data.length === 0 ? (
                <div className="p-10 text-center">
                  {archived ? (
                    <Archive className="mx-auto size-8 text-burgundy" />
                  ) : processed ? (
                    <CheckCircle2 className="mx-auto size-8 text-burgundy" />
                  ) : (
                    <UserPlus className="mx-auto size-8 text-burgundy" />
                  )}
                  <p className="mt-3 font-semibold">
                    {filtersActive
                      ? archived
                        ? 'No archived applicants match these filters.'
                        : processed
                          ? 'No processed applications match these filters.'
                          : 'No applicants match these filters.'
                      : archived
                        ? 'No applicants have been archived yet.'
                        : processed
                          ? 'No applications have been processed yet.'
                          : 'No applicants have been added yet.'}
                  </p>
                  <p className="text-ink/65">
                    {filtersActive
                      ? 'Try adjusting your search or stage filter.'
                      : archived
                        ? 'Applicants appear here once archived from their record.'
                        : processed
                          ? 'Applications appear here automatically once every stage is complete.'
                          : 'Create an applicant after their external credential submission is received.'}
                  </p>
                </div>
              ) : (
                applicants.data?.data.map((person) => (
                  <button
                    key={person.id}
                    className="schd-row-hover grid w-full items-center gap-1 border-b bg-surface p-4 text-left hover:bg-burgundy-soft focus-visible:bg-burgundy-soft md:grid-cols-[1.4fr_1.3fr_1fr_1fr_20px] md:gap-4"
                    onClick={() => setSelectedId(person.id)}
                  >
                    <span>
                      <span className="flex flex-wrap items-center gap-x-2">
                        <strong>
                          {person.name.firstName} {person.name.lastName}
                        </strong>
                        {archived && <StatusBadge tone="neutral">Archived</StatusBadge>}
                        {processed && <StatusBadge tone="neutral">Processed</StatusBadge>}
                      </span>
                      <small className="block text-ink/65">{person.email}</small>
                    </span>
                    <span>
                      <span className="block text-xs font-semibold text-ink/65 md:hidden">
                        Stage
                      </span>
                      {person.activeApplication ? (
                        <>
                          <strong>{person.activeApplication.currentStage.name}</strong>
                          <StageProgress
                            stages={stages.data?.data ?? []}
                            currentSortOrder={person.activeApplication.currentStage.sortOrder}
                          />
                        </>
                      ) : (
                        'No active application'
                      )}
                    </span>
                    <span>
                      <span className="block text-xs font-semibold text-ink/65 md:hidden">
                        Assigned
                      </span>
                      {person.primaryAssignment?.name ?? 'Unassigned'}
                    </span>
                    <span>
                      <span className="block text-xs font-semibold text-ink/65 md:hidden">
                        Updated
                      </span>
                      {new Date(person.updatedAt).toLocaleDateString()}
                    </span>
                    <ChevronRight
                      className="hidden size-4 shrink-0 text-ink/30 md:block"
                      aria-hidden="true"
                    />
                  </button>
                ))
              )}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-ink/65">
                {applicants.data?.meta?.total ?? 0} total
                {(applicants.data?.meta?.total ?? 0) > 15 &&
                  ` · Page ${page} of ${Math.max(1, Math.ceil((applicants.data?.meta?.total ?? 0) / 15))}`}
              </span>
              <div className="flex gap-2">
                <button
                  className="grid size-11 place-items-center rounded-control border disabled:opacity-40"
                  aria-label="Previous page"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft />
                </button>
                <button
                  className="grid size-11 place-items-center rounded-control border disabled:opacity-40"
                  aria-label="Next page"
                  disabled={page * 15 >= (applicants.data?.meta?.total ?? 0)}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight />
                </button>
              </div>
            </div>
          </>
        )}
        </div>
        )}
      </section>
      {showCreate && (
        <CreateApplicant
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            setSelectedId(id);
            refresh();
          }}
        />
      )}
      {changePasswordOpen && (
        <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />
      )}
    </main>
  );
}

function SettingsPanel() {
  const queryClient = useQueryClient();
  const [section, setSection] = useState<
    'integrations' | 'templates' | 'workflow' | 'people' | 'security'
  >(
    'integrations',
  );
  const templates = useQuery({
    queryKey: ['settings', 'templates'],
    queryFn: () => api<{ data: NotificationTemplate[] }>('/settings/notification-templates'),
    enabled: section === 'templates',
    staleTime: 60_000,
  });
  const stages = useQuery({
    queryKey: ['workflow-stages'],
    queryFn: () => api<{ data: WorkflowStage[] }>('/settings/workflow-stages'),
    enabled: section === 'workflow' || section === 'integrations',
    staleTime: 5 * 60_000,
  });
  const calcomConnection = useQuery({
    queryKey: ['settings', 'integrations', 'calcom'],
    queryFn: () => api<CalcomConnection>('/settings/integrations/calcom'),
    enabled: section === 'integrations',
  });
  const outlookConnection = useQuery({
    queryKey: ['settings', 'integrations', 'outlook'],
    queryFn: () => api<OutlookConnection>('/settings/integrations/outlook'),
    enabled: section === 'integrations',
  });
  const clicksendConnection = useQuery({
    queryKey: ['settings', 'integrations', 'clicksend'],
    queryFn: () => api<ClickSendConnection>('/settings/integrations/clicksend'),
    enabled: section === 'integrations',
  });
  const appointmentTypes = useQuery({
    queryKey: ['settings', 'appointment-types'],
    queryFn: () => api<{ data: AppointmentType[] }>('/settings/appointment-types'),
    enabled: section === 'integrations',
    staleTime: 5 * 60_000,
  });
  const users = useQuery({
    queryKey: ['settings', 'users'],
    queryFn: () => api<{ data: StaffUser[] }>('/settings/users'),
    enabled: section === 'people',
  });
  const notices = useQuery({
    queryKey: ['settings', 'notices'],
    queryFn: () => api<{ data: Notice[] }>('/settings/notices'),
    enabled: section === 'workflow',
  });
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api<Session>('/auth/session'),
  });
  const [inviteStaffOpen, setInviteStaffOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [createAppointmentTypeOpen, setCreateAppointmentTypeOpen] = useState(false);
  const [editingAppointmentTypeId, setEditingAppointmentTypeId] = useState<string | null>(null);
  const nav = [
    ['integrations', 'Integrations'],
    ['templates', 'Notifications'],
    ['workflow', 'Application Stage'],
    ['people', 'Staff access'],
    ['security', 'Change password'],
  ] as const;
  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6 flex items-center gap-2">
        <Settings className="size-5 text-burgundy" aria-hidden="true" />
        <h2 className="font-display text-2xl sm:text-3xl">Settings</h2>
        <InfoTip label="About settings">
          Configure integrations, notification templates, the application stage pipeline, and
          staff access from the tabs below.
        </InfoTip>
      </header>
      <nav
        className="flex gap-1 overflow-x-auto border-b border-border"
        aria-label="Settings sections"
      >
        {nav.map(([id, label]) => (
          <button
            key={id}
            aria-current={section === id ? 'page' : undefined}
            className={`shrink-0 border-b-2 px-4 py-3 text-sm font-semibold ${
              section === id
                ? 'border-burgundy text-burgundy'
                : 'border-transparent text-ink/60 hover:text-ink'
            }`}
            onClick={() => setSection(id)}
          >
            {label}
          </button>
        ))}
      </nav>
      <section key={section} className="schd-tab-fade-in mt-6 min-w-0">
            {section === 'integrations' && (
              <div className="space-y-5">
                <div className="mb-1 flex items-center gap-2">
                  <Plug className="size-5 text-burgundy" aria-hidden="true" />
                  <h3 className="text-lg font-semibold">Integrations</h3>
                  <InfoTip label="About integrations">
                    Only supported providers are available here. Credentials are write-only — once
                    saved, they can be replaced but never viewed again. Appointment types map
                    Cal.com event types to interview or exam bookings and the workflow stages that
                    make an applicant eligible for them, so they're listed nested under Cal.com.
                  </InfoTip>
                </div>
                <section className="overflow-hidden rounded-card border border-border bg-surface">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-left text-sm">
                      <thead>
                        <tr className="border-b border-border bg-surface-muted text-xs font-semibold uppercase tracking-wide text-ink/60">
                          <th className="w-[30%] px-4 py-3 font-semibold">Service</th>
                          <th className="w-[35%] px-4 py-3 font-semibold">Account &amp; identifier</th>
                          <th className="w-[20%] px-4 py-3 font-semibold">Status</th>
                          <th className="w-[15%] px-4 py-3 font-semibold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <OutlookConnectionForm
                          connection={outlookConnection}
                          onSaved={() =>
                            queryClient.invalidateQueries({
                              queryKey: ['settings', 'integrations', 'outlook'],
                            })
                          }
                        />
                        <ClickSendConnectionForm
                          connection={clicksendConnection}
                          onSaved={() =>
                            queryClient.invalidateQueries({
                              queryKey: ['settings', 'integrations', 'clicksend'],
                            })
                          }
                        />
                        <CalcomConnectionForm
                          connection={calcomConnection}
                          onSaved={() =>
                            queryClient.invalidateQueries({
                              queryKey: ['settings', 'integrations', 'calcom'],
                            })
                          }
                        />
                        <tr>
                          <td colSpan={4} className="px-4 py-2 pl-11">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-sm font-semibold text-burgundy hover:underline"
                              onClick={() => setCreateAppointmentTypeOpen(true)}
                            >
                              <Plus className="size-4" aria-hidden="true" /> New appointment type
                            </button>
                          </td>
                        </tr>
                        {createAppointmentTypeOpen && (
                          <tr>
                            <td colSpan={4} className="p-0">
                              <AppointmentTypeForm
                                stages={stages.data?.data ?? []}
                                onCancel={() => setCreateAppointmentTypeOpen(false)}
                                onSaved={() => {
                                  setCreateAppointmentTypeOpen(false);
                                  void queryClient.invalidateQueries({
                                    queryKey: ['settings', 'appointment-types'],
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        )}
                        {appointmentTypes.isLoading ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-4" role="status">
                              Loading appointment types…
                            </td>
                          </tr>
                        ) : appointmentTypes.isError ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-4">
                              <ErrorRetry query={appointmentTypes} />
                            </td>
                          </tr>
                        ) : appointmentTypes.data?.data.length ? (
                          appointmentTypes.data.data.map((item) =>
                            editingAppointmentTypeId === item.id ? (
                              <tr key={item.id}>
                                <td colSpan={4} className="p-0">
                                  <AppointmentTypeForm
                                    stages={stages.data?.data ?? []}
                                    appointmentType={item}
                                    onCancel={() => setEditingAppointmentTypeId(null)}
                                    onSaved={() => {
                                      setEditingAppointmentTypeId(null);
                                      void queryClient.invalidateQueries({
                                        queryKey: ['settings', 'appointment-types'],
                                      });
                                    }}
                                  />
                                </td>
                              </tr>
                            ) : (
                              <tr key={item.id} className="align-top">
                                <td className="px-4 py-4">
                                  <div className="flex items-start gap-2 pl-6">
                                    <span className="text-ink/30" aria-hidden="true">
                                      ↳
                                    </span>
                                    <div>
                                      <p className="font-semibold">{item.name.en}</p>
                                      <p className="text-xs capitalize text-ink/55">
                                        {item.type ?? 'unmapped'}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4">
                                  <p className="font-medium break-all">
                                    {item.eventTypeSlug ?? item.eventTypeId}
                                  </p>
                                  <p className="text-xs text-ink/55">
                                    {item.durationMinutes
                                      ? `${item.durationMinutes} min`
                                      : 'No duration set'}
                                  </p>
                                </td>
                                <td className="px-4 py-4">
                                  <StatusBadge tone={templateActiveTone(item.active)}>
                                    {item.active ? 'active' : 'inactive'}
                                  </StatusBadge>
                                </td>
                                <td className="px-4 py-4 text-right">
                                  <ActionMenu
                                    items={[
                                      {
                                        label: 'Edit',
                                        onClick: () => setEditingAppointmentTypeId(item.id),
                                      },
                                    ]}
                                  />
                                </td>
                              </tr>
                            ),
                          )
                        ) : (
                          <tr>
                            <td colSpan={4} className="px-4 py-4 pl-11 text-ink/65">
                              No appointment types configured yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}
            {section === 'templates' && (
              <div>
                {templates.isLoading ? (
                  <div className="space-y-3" role="status">
                    <span className="sr-only">Loading templates</span>
                    <div className="schd-skeleton h-5 w-2/5" aria-hidden="true" />
                    <div className="schd-skeleton h-24 w-full" aria-hidden="true" />
                    <div className="schd-skeleton h-24 w-full" aria-hidden="true" />
                  </div>
                ) : templates.isError ? (
                  <ErrorRetry query={templates} />
                ) : (
                  <NotificationsPanel
                    templates={templates.data?.data ?? []}
                    onSaved={() => queryClient.invalidateQueries({ queryKey: ['settings', 'templates'] })}
                  />
                )}
              </div>
            )}
            {section === 'workflow' && (
              <div>
                {stages.isLoading ? (
                  <div className="space-y-3" role="status">
                    <span className="sr-only">Loading the application pipeline</span>
                    <div className="schd-skeleton h-5 w-2/5" aria-hidden="true" />
                    <div className="schd-skeleton h-12 w-full" aria-hidden="true" />
                    <div className="schd-skeleton h-12 w-full" aria-hidden="true" />
                    <div className="schd-skeleton h-12 w-full" aria-hidden="true" />
                  </div>
                ) : stages.isError ? (
                  <ErrorRetry query={stages} />
                ) : (
                  <>
                    <div className="mb-5 flex items-center gap-2">
                      <Workflow className="size-5 text-burgundy" aria-hidden="true" />
                      <h3 className="text-lg font-semibold">Application Stage</h3>
                      <InfoTip label="About application stages">
                        This is the pipeline every application moves through, in order. Pick a
                        stage on the left to edit its name or notices, or add a new one anywhere in
                        the sequence — changes apply everywhere the stage is shown immediately. A
                        stage can only be deleted if no applicant has ever been in it. Staff can
                        always move an application forward, skip ahead, or move it back to an
                        earlier stage; a reason is only required when skipping or moving back. Each
                        stage can also have its own notices, shown to applicants currently in that
                        stage.
                      </InfoTip>
                    </div>
                    <WorkflowStagesPanel
                      stages={stages.data?.data ?? []}
                      notices={notices.data?.data ?? []}
                      onStagesChanged={() =>
                        queryClient.invalidateQueries({ queryKey: ['workflow-stages'] })
                      }
                      onNoticesChanged={() =>
                        queryClient.invalidateQueries({ queryKey: ['settings', 'notices'] })
                      }
                    />
                  </>
                )}
              </div>
            )}
            {section === 'people' && (
              <div>
                {users.isLoading ? (
                  <div className="space-y-3" role="status">
                    <span className="sr-only">Loading staff access</span>
                    <div className="schd-skeleton h-5 w-2/5" aria-hidden="true" />
                    <div className="schd-skeleton h-12 w-full" aria-hidden="true" />
                    <div className="schd-skeleton h-12 w-full" aria-hidden="true" />
                  </div>
                ) : users.isError ? (
                  <ErrorRetry query={users} />
                ) : (
                  <>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Users className="size-5 text-burgundy" aria-hidden="true" />
                        <h3 className="text-lg font-semibold">Staff access</h3>
                        <InfoTip label="About staff access">
                          The last active administrator can't be demoted or deactivated, so the
                          district always keeps at least one. Every staff and admin account is
                          assignable to applicants.
                        </InfoTip>
                      </div>
                      <button
                        className="schd-button-primary shrink-0"
                        onClick={() => setInviteStaffOpen(true)}
                      >
                        <UserPlus className="size-5" /> Invite staff
                      </button>
                    </div>
                    <section className="overflow-hidden rounded-card border border-border bg-surface">
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-left text-sm">
                          <thead>
                            <tr className="border-b border-border bg-surface-muted text-xs font-semibold uppercase tracking-wide text-ink/60">
                              <th className="w-[40%] px-4 py-3 font-semibold">User</th>
                              <th className="w-[20%] px-4 py-3 font-semibold">Role</th>
                              <th className="w-[20%] px-4 py-3 font-semibold">Status</th>
                              <th className="w-[20%] px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {users.data?.data.length ? (
                              users.data.data.map((u) => (
                                <StaffAccessRow
                                  key={u.id}
                                  user={u}
                                  isSelf={u.id === session.data?.user.id}
                                  onEdit={() => setEditingUserId(u.id)}
                                  onRemove={() => setRemovingUserId(u.id)}
                                />
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4} className="px-4 py-6 text-center text-sm text-ink/60">
                                  No staff members yet.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  </>
                )}
                {inviteStaffOpen && (
                  <StaffInviteModal
                    onCancel={() => setInviteStaffOpen(false)}
                    onCreated={() => {
                      setInviteStaffOpen(false);
                      void queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
                    }}
                  />
                )}
                {editingUserId &&
                  (() => {
                    const editingUser = users.data?.data.find((u) => u.id === editingUserId);
                    if (!editingUser) return null;
                    return (
                      <StaffEditModal
                        user={editingUser}
                        onCancel={() => setEditingUserId(null)}
                        onSaved={() => {
                          setEditingUserId(null);
                          void queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
                        }}
                      />
                    );
                  })()}
                {removingUserId &&
                  (() => {
                    const removingUser = users.data?.data.find((u) => u.id === removingUserId);
                    if (!removingUser) return null;
                    return (
                      <RemoveStaffModal
                        user={removingUser}
                        onCancel={() => setRemovingUserId(null)}
                        onRemoved={() => {
                          setRemovingUserId(null);
                          void queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
                        }}
                      />
                    );
                  })()}
              </div>
            )}
            {section === 'security' && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <KeyRound className="size-5 text-burgundy" aria-hidden="true" />
                  <h3 className="text-lg font-semibold">Change password</h3>
                  <InfoTip label="About change password">
                    You'll need your current password to confirm the change. You remain signed in
                    on this device afterward.
                  </InfoTip>
                </div>
                <ChangePasswordForm />
              </div>
            )}
      </section>
    </div>
  );
}

function ErrorRetry({ query }: { query: { error: unknown; refetch: () => unknown } }) {
  return (
    <p role="alert">
      {errorMessage(query.error)}{' '}
      <button className="underline" onClick={() => void query.refetch()}>
        Retry
      </button>
    </p>
  );
}

function CreateApplicant({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { closing, requestClose } = useModalClosing(onClose);
  const dialogRef = useDialogFocus(requestClose);
  const stages = useQuery({
    queryKey: ['workflow-stages'],
    queryFn: () => api<{ data: Stage[] }>('/settings/workflow-stages'),
    staleTime: 5 * 60_000,
  });
  const [submitting, setSubmitting] = useState(false);
  const [language, setLanguage] = useState('en');
  const [stageId, setStageId] = useState('');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stageId) return;
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    try {
      const created = await api<Applicant>('/applicants', {
        method: 'POST',
        body: JSON.stringify({
          name: {
            firstName: form.get('firstName'),
            middleName: form.get('middleName') || null,
            lastName: form.get('lastName'),
          },
          email: form.get('email'),
          phone: form.get('phone') || null,
          preferredLanguage: language,
          initialWorkflowStageId: stageId,
        }),
      });
      toast.success('Applicant and initial application created.');
      onCreated(created.id);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-20 flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-title"
    >
      <form
        onSubmit={submit}
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} max-h-[90vh] w-full max-w-xl overflow-auto bg-surface p-6 shadow-card`}
      >
        <div className="flex justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-burgundy">External submission</p>
            <h2 id="create-title" className="font-display text-xl">
              Create applicant
            </h2>
          </div>
          <button type="button" onClick={requestClose} className="schd-button-secondary">
            Cancel
          </button>
        </div>
        <p className="mt-3 text-sm text-ink/70">
          Applicants don't apply here — add this record after staff receive their application
          externally.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {[
            ['firstName', 'First name (required)'],
            ['middleName', 'Middle name (optional)'],
            ['lastName', 'Last name (required)'],
            ['email', 'Email address (required)'],
            ['phone', 'Phone (optional)'],
          ].map(([field, label]) => (
            <label key={field} className="text-sm font-semibold">
              {label}
              <input
                required={field !== 'middleName' && field !== 'phone'}
                name={field}
                type={field === 'email' ? 'email' : 'text'}
                className="mt-1 block min-h-11 w-full rounded-control border p-2 font-normal"
              />
            </label>
          ))}
          <label className="text-sm font-semibold">
            Preferred language
            <div className="mt-1 font-normal">
              <SearchableSelect
                value={language}
                onChange={setLanguage}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'es', label: 'Español' },
                ]}
              />
            </div>
          </label>
          <label className="text-sm font-semibold">
            Initial workflow stage (required)
            <div className="mt-1 font-normal">
              <SearchableSelect
                value={stageId}
                onChange={setStageId}
                required
                disabled={stages.isLoading || stages.isError}
                placeholder={stages.isLoading ? 'Loading stages…' : 'Select a stage'}
                options={
                  stages.data?.data.map((stage) => ({
                    value: stage.id,
                    label: stage.translations.en.name,
                  })) ?? []
                }
              />
            </div>
            {stages.isError && <span className="text-red-700">Stages could not load.</span>}
          </label>
        </div>
        <button disabled={submitting || stages.isLoading} className="schd-button-primary mt-6">
          {submitting ? (
            'Creating…'
          ) : (
            <>
              <UserPlus className="size-5" /> Create applicant
            </>
          )}
        </button>
      </form>
    </div>
  );
}

const applicantTabs = [
  ['overview', 'Overview'],
  ['status', 'Status & history'],
  ['documents', 'Documents'],
  ['shared-resources', 'Shared resources'],
  ['appointments', 'Appointments'],
  ['team', 'Team & notes'],
] as const;
type ApplicantTab = (typeof applicantTabs)[number][0];

function NoActiveApplication() {
  return (
    <p className="rounded-card border border-dashed border-border bg-surface p-6 text-center text-sm text-ink/65">
      This applicant has no active application yet, so there is nothing to show here.
    </p>
  );
}

function ApplicantPanel({
  applicant,
  loading,
  error,
  isAdmin,
  onClose,
  onRefresh,
}: {
  applicant?: Applicant;
  loading: boolean;
  error: unknown;
  isAdmin: boolean;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmPermanentDelete, setConfirmPermanentDelete] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [tab, setTab] = useState<ApplicantTab>('overview');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const uploadPhoto = async (file: File) => {
    if (!applicant) return;
    setUploadingPhoto(true);
    try {
      await uploadAvatar(`/applicants/${applicant.id}/avatar`, file);
      toast.success('Profile photo updated.');
      onRefresh();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setUploadingPhoto(false);
    }
  };
  const act = async (
    path: string,
    successMessage: string,
    method = 'POST',
    body?: unknown,
  ) => {
    setBusy(true);
    try {
      await api(path, { method, body: body ? JSON.stringify(body) : undefined });
      toast.success(successMessage);
      onRefresh();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const permanentlyDelete = async () => {
    if (!applicant) return;
    setBusy(true);
    try {
      await api(`/applicants/${applicant.id}/permanently-delete`, {
        method: 'POST',
        body: JSON.stringify({ confirmEmail: applicant.email, confirmed: true }),
      });
      toast.success('Applicant permanently deleted.');
      setConfirmPermanentDelete(false);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto max-w-4xl">
      <button
        className="inline-flex items-center gap-2 text-sm font-semibold text-burgundy hover:underline"
        onClick={onClose}
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Back to applicants
      </button>
      {loading ? (
        <p className="mt-8" role="status">
          Loading applicant…
        </p>
      ) : error ? (
        <p className="mt-8" role="alert">
          {errorMessage(error)}
        </p>
      ) : (
        applicant && (
          <>
            <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <Avatar
                  photoUrl={
                    applicant.avatarUpdatedAt
                      ? `${apiBaseUrl}/applicants/${applicant.id}/avatar?v=${encodeURIComponent(applicant.avatarUpdatedAt)}`
                      : null
                  }
                  initials={initialsFromName(`${applicant.name.firstName} ${applicant.name.lastName}`)}
                  size={64}
                  editable
                  uploading={uploadingPhoto}
                  uploadLabel="Upload profile photo"
                  onSelectFile={(file) => void uploadPhoto(file)}
                />
                <div>
                <p className="text-sm font-semibold text-burgundy">Applicant profile</p>
                <h2 id="applicant-detail-title" className="flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-2xl sm:text-3xl">
                  {applicant.name.firstName} {applicant.name.lastName}
                  {applicant.archivedAt && (
                    <span className="text-base">
                      <StatusBadge tone="neutral">Archived</StatusBadge>
                    </span>
                  )}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink/70">
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="size-4 text-ink/45" aria-hidden="true" />
                    {applicant.email}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="size-4 text-ink/45" aria-hidden="true" />
                    {applicant.phone ?? 'No phone on file'}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Languages className="size-4 text-ink/45" aria-hidden="true" />
                    {applicant.preferredLanguage === 'es' ? 'Español' : 'English'}
                  </span>
                </div>
                {applicant.activeApplication && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink/70">
                    <span className="inline-flex items-center gap-1.5">
                      <Workflow className="size-4 text-ink/45" aria-hidden="true" />
                      <strong>{applicant.activeApplication.currentStage.name}</strong>
                    </span>
                    <StatusBadge tone={applicationLifecycleTone(applicant.activeApplication.lifecycle)}>
                      {applicant.activeApplication.lifecycle}
                    </StatusBadge>
                  </div>
                )}
                </div>
              </div>
              <div className="flex flex-wrap shrink-0 justify-end gap-2">
                <button
                  className="schd-button-secondary-sm"
                  disabled={busy}
                  onClick={() => setEditingProfile(true)}
                >
                  <Pencil className="size-4" aria-hidden="true" /> Edit
                </button>
                {applicant.archivedAt ? (
                  <>
                    <button
                      className="schd-button-secondary-sm"
                      disabled={busy}
                      onClick={() => act(`/applicants/${applicant.id}/reactivate`, 'Applicant reactivated.')}
                    >
                      <RotateCcw className="size-4" aria-hidden="true" /> Reactivate
                    </button>
                    {isAdmin && (
                      <button
                        className="schd-button-destructive-sm"
                        disabled={busy}
                        onClick={() => setConfirmPermanentDelete(true)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" /> Delete permanently
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    className="schd-button-destructive-sm"
                    disabled={busy}
                    onClick={() => setConfirmArchive(true)}
                  >
                    <Archive className="size-4" aria-hidden="true" /> Archive
                  </button>
                )}
              </div>
            </div>
            <nav
              className="mt-6 flex gap-1 overflow-x-auto border-b border-border"
              aria-label="Applicant record sections"
            >
              {applicantTabs.map(([id, label]) => (
                <button
                  key={id}
                  aria-current={tab === id ? 'page' : undefined}
                  className={`shrink-0 border-b-2 px-4 py-3 text-sm font-semibold ${
                    tab === id
                      ? 'border-burgundy text-burgundy'
                      : 'border-transparent text-ink/60 hover:text-ink'
                  }`}
                  onClick={() => setTab(id)}
                >
                  {label}
                </button>
              ))}
            </nav>
            <div className="mt-6">
              {tab === 'overview' && (
                <OverviewDashboard applicant={applicant} onRefresh={onRefresh} onNavigate={setTab} />
              )}
              {tab === 'status' &&
                (applicant.activeApplication ? (
                  <StatusManager application={applicant.activeApplication} onSaved={onRefresh} />
                ) : (
                  <NoActiveApplication />
                ))}
              {tab === 'documents' &&
                (applicant.activeApplication ? (
                  <DocumentManager applicationId={applicant.activeApplication.id} />
                ) : (
                  <NoActiveApplication />
                ))}
              {tab === 'shared-resources' &&
                (applicant.activeApplication ? (
                  <SharedResourceManager applicationId={applicant.activeApplication.id} />
                ) : (
                  <NoActiveApplication />
                ))}
              {tab === 'appointments' &&
                (applicant.activeApplication ? (
                  <AppointmentsPanel applicationId={applicant.activeApplication.id} />
                ) : (
                  <NoActiveApplication />
                ))}
              {tab === 'team' &&
                (applicant.activeApplication ? (
                  <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
                    <section className="rounded-card border bg-surface p-4 sm:p-5">
                      <AssignmentManager
                        application={applicant.activeApplication}
                        onSaved={onRefresh}
                      />
                    </section>
                    <section className="rounded-card border bg-surface p-4 sm:p-5">
                      <Notes
                        applicationId={applicant.activeApplication.id}
                        note={note}
                        setNote={setNote}
                        busy={busy}
                        setBusy={setBusy}
                      />
                    </section>
                  </div>
                ) : (
                  <NoActiveApplication />
                ))}
            </div>
          </>
        )
      )}
      {confirmArchive && applicant && (
        <ArchiveConfirmation
          applicant={applicant}
          busy={busy}
          onCancel={() => setConfirmArchive(false)}
          onConfirm={() => {
            setConfirmArchive(false);
            void act(`/applicants/${applicant.id}/archive`, 'Applicant archived.', 'POST', {
              reason: 'Archived by staff',
            });
          }}
        />
      )}
      {confirmPermanentDelete && applicant && (
        <PermanentDeleteConfirmation
          applicant={applicant}
          busy={busy}
          onCancel={() => setConfirmPermanentDelete(false)}
          onConfirm={() => void permanentlyDelete()}
        />
      )}
      {editingProfile && applicant && (
        <ProfileEditModal
          applicant={applicant}
          onCancel={() => setEditingProfile(false)}
          onSaved={() => {
            setEditingProfile(false);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

function ArchiveConfirmation({
  applicant,
  busy,
  onCancel,
  onConfirm,
}: {
  applicant: Applicant;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-30 flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="archive-title"
    >
      <section className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-w-md`}>
        <p className="schd-eyebrow">Confirm archival</p>
        <h3 id="archive-title" className="mt-2 text-xl font-semibold">
          Archive {applicant.name.firstName} {applicant.name.lastName}?
        </h3>
        <p className="mt-3 text-sm text-ink/70">
          This removes them from your active applicants list and they won't be able to use the
          applicant portal. Their history is kept, and any pending portal invitation is revoked.
          You can reactivate them and restore portal access at any time.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button className="schd-button-secondary" onClick={requestClose}>
            Keep applicant active
          </button>
          <button className="schd-button-destructive-strong" disabled={busy} onClick={onConfirm}>
            Archive applicant
          </button>
        </div>
      </section>
    </div>
  );
}

function PermanentDeleteConfirmation({
  applicant,
  busy,
  onCancel,
  onConfirm,
}: {
  applicant: Applicant;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [confirmEmail, setConfirmEmail] = useState('');
  const matches = confirmEmail.trim().toLowerCase() === applicant.email.toLowerCase();
  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-30 flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="permanent-delete-title"
    >
      <section className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-w-md`}>
        <p className="schd-eyebrow text-red-700">Permanent, irreversible deletion</p>
        <h3 id="permanent-delete-title" className="mt-2 text-xl font-semibold">
          Permanently delete {applicant.name.firstName} {applicant.name.lastName}?
        </h3>
        <p className="mt-3 text-sm text-ink/70">
          This erases the applicant, every application, document, appointment, note, and message
          history associated with them -- including records normally kept forever for audit
          purposes. There is no undo and no way to recover this data afterward. Archiving (which
          you can still reverse) is almost always the better choice.
        </p>
        <label className="mt-4 block text-sm font-medium text-ink">
          Type their email ({applicant.email}) to confirm
          <input
            autoFocus
            value={confirmEmail}
            onChange={(event) => setConfirmEmail(event.target.value)}
            className="mt-1 block min-h-10 w-full rounded-control border border-border bg-surface px-3 font-normal"
            placeholder={applicant.email}
          />
        </label>
        <div className="mt-5 flex flex-wrap gap-3">
          <button className="schd-button-secondary" onClick={requestClose}>
            Cancel
          </button>
          <button
            className="schd-button-destructive-strong disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy || !matches}
            onClick={onConfirm}
          >
            {busy ? 'Deleting…' : 'Permanently delete'}
          </button>
        </div>
      </section>
    </div>
  );
}

const CANCELLABLE_APPOINTMENT_STATUSES = ['pending_confirmation', 'confirmed', 'rescheduled'];

function AppointmentsPanel({ applicationId }: { applicationId: string }) {
  const appointments = useQuery({
    queryKey: ['application-appointments', applicationId],
    queryFn: () => api<{ data: Appointment[] }>(`/applications/${applicationId}/appointments`),
  });
  // Staff/admin equivalent of the applicant's own /me/scheduling handoff -- lets staff book an
  // interview or exam on the applicant's behalf, using the same Cal.com booking page and workflow-
  // stage eligibility the applicant would see. Unlike the applicant view, the book options here
  // are always offered regardless of whether a live appointment of that type already exists.
  const scheduling = useQuery({
    queryKey: ['application-scheduling', applicationId],
    queryFn: () => api<SchedulingHandoff>(`/applications/${applicationId}/scheduling`),
  });
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const cancelAppointment = async (appointmentId: string) => {
    if (!cancelReason.trim()) return;
    setCancelBusy(true);
    try {
      await api(`/applications/${applicationId}/appointments/${appointmentId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: cancelReason.trim() }),
      });
      toast.success('Appointment cancelled.');
      setCancellingId(null);
      setCancelReason('');
      await Promise.all([appointments.refetch(), scheduling.refetch()]);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCancelBusy(false);
    }
  };

  const [rescheduleTargetId, setRescheduleTargetId] = useState<string | null>(null);
  const [bookingTypeId, setBookingTypeId] = useState<string | null>(null);
  const embedRef = useRef<HTMLDivElement>(null);
  const closeEmbed = () => {
    setRescheduleTargetId(null);
    setBookingTypeId(null);
  };
  const startReschedule = (appointmentId: string) => {
    setBookingTypeId(null);
    setRescheduleTargetId(appointmentId);
    embedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const startBooking = (appointmentTypeId: string) => {
    setRescheduleTargetId(null);
    setBookingTypeId(appointmentTypeId);
    embedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const refreshAfterBooking = () => {
    closeEmbed();
    void appointments.refetch();
    void scheduling.refetch();
  };

  const existing = appointments.data?.data ?? [];
  const mappings = scheduling.data?.eventMappings ?? [];
  const rescheduleTarget = existing.find((a) => a.id === rescheduleTargetId) ?? null;
  // Reschedule reuses the same inline embed as booking a new appointment -- it's just pointed at
  // the existing appointment's reschedule URL instead of a bookable type's booking URL.
  const rescheduleMapping = useMemo(
    () =>
      rescheduleTarget && rescheduleTarget.bookingUrl
        ? {
            appointmentTypeId: rescheduleTarget.appointmentTypeId,
            name: rescheduleTarget.typeName,
            description: null,
            embedUrl: rescheduleTarget.bookingUrl,
            fallbackUrl: rescheduleTarget.bookingUrl,
          }
        : null,
    [rescheduleTarget],
  );
  const bookingMapping = mappings.find((m) => m.appointmentTypeId === bookingTypeId) ?? null;
  const activeMapping = rescheduleMapping ?? bookingMapping;

  return (
    <div className="space-y-5">
      <section className="rounded-card border bg-surface p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Appointments</h3>
          <CalendarClock className="size-5 text-burgundy" />
        </div>
        {appointments.isLoading ? (
          <p className="mt-2 text-sm" role="status">
            Loading appointments…
          </p>
        ) : appointments.isError ? (
          <div className="schd-error mt-2 text-sm" role="alert">
            {errorMessage(appointments.error)}{' '}
            <button className="underline" onClick={() => void appointments.refetch()}>
              Retry
            </button>
          </div>
        ) : appointments.data?.data.length ? (
          <ul className="mt-3 space-y-3">
            {appointments.data.data.map((a) => {
              const cancellable = CANCELLABLE_APPOINTMENT_STATUSES.includes(a.status);
              return (
                <li key={a.id} className="rounded-control border border-border p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold">{a.typeName}</p>
                      <p className="mt-0.5 text-ink/65">{new Date(a.startsAt).toLocaleString()}</p>
                    </div>
                    <StatusBadge tone={appointmentStatusTone(a.status)}>
                      {appointmentStatusLabel(a.status)}
                    </StatusBadge>
                  </div>
                  {cancellable && (
                    <div className="mt-2 flex items-center gap-4 border-t border-border pt-2">
                      {a.bookingUrl && (
                        <button
                          type="button"
                          className="schd-link"
                          onClick={() => startReschedule(a.id)}
                        >
                          Reschedule
                        </button>
                      )}
                      <button
                        type="button"
                        className="schd-button-destructive-sm"
                        onClick={() => {
                          setCancellingId(a.id);
                          setCancelReason('');
                        }}
                      >
                        Cancel appointment
                      </button>
                    </div>
                  )}
                  {cancellingId === a.id && (
                    <div className="mt-2 rounded-control border border-red-200 bg-red-50 p-2.5">
                      <p className="text-sm text-red-900">
                        This cancels the {a.typeName} appointment on{' '}
                        {new Date(a.startsAt).toLocaleString()}. The applicant will be notified.
                      </p>
                      <div className="mt-2 flex flex-wrap items-end gap-2">
                        <label className="min-w-0 flex-1 text-sm text-red-900">
                          Cancellation reason (required)
                          <input
                            required
                            value={cancelReason}
                            onChange={(event) => setCancelReason(event.target.value)}
                            className="mt-1 block min-h-9 w-full rounded-control border bg-white px-2 font-normal text-ink"
                          />
                        </label>
                        <button
                          type="button"
                          disabled={cancelBusy || !cancelReason.trim()}
                          className="schd-button-destructive-strong min-h-9 disabled:opacity-50"
                          onClick={() => void cancelAppointment(a.id)}
                        >
                          {cancelBusy ? 'Cancelling…' : 'Confirm cancellation'}
                        </button>
                        <button
                          type="button"
                          className="min-h-9 px-2 text-sm underline"
                          onClick={() => setCancellingId(null)}
                        >
                          Keep appointment
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-ink/65">No appointments scheduled.</p>
        )}
      </section>

      <section className="rounded-card border bg-surface p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Book an appointment</h3>
          <CalendarPlus className="size-5 text-burgundy" />
        </div>
        <p className="mt-1 text-sm text-ink/65">
          Book an interview or exam for this applicant using the same Cal.com flow they use --
          the applicant is notified just as if they had booked it themselves.
        </p>
        {scheduling.isLoading ? (
          <p className="mt-3 text-sm" role="status">
            Loading available appointment types…
          </p>
        ) : scheduling.isError ? (
          <div className="schd-error mt-3 text-sm" role="alert">
            {errorMessage(scheduling.error)}{' '}
            <button className="underline" onClick={() => void scheduling.refetch()}>
              Retry
            </button>
          </div>
        ) : mappings.length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {mappings.map((mapping) => (
              <button
                key={mapping.appointmentTypeId}
                type="button"
                onClick={() => startBooking(mapping.appointmentTypeId)}
                aria-pressed={bookingMapping?.appointmentTypeId === mapping.appointmentTypeId}
                className={`min-h-9 rounded-control px-3 text-sm font-semibold ${
                  bookingMapping?.appointmentTypeId === mapping.appointmentTypeId
                    ? 'bg-burgundy text-white'
                    : 'border border-border bg-surface text-ink hover:bg-surface-muted'
                }`}
              >
                Book {mapping.name}
              </button>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-ink/65">
            {scheduling.data?.message ?? 'No appointment types are available at this stage.'}
          </p>
        )}
      </section>

      {activeMapping && (
        <section ref={embedRef} className="rounded-card border bg-surface p-4 sm:p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold">
              {rescheduleMapping ? 'Reschedule' : 'Book'} — {activeMapping.name}
            </h3>
            <div className="flex items-center gap-3">
              <button type="button" className="schd-link text-sm" onClick={refreshAfterBooking}>
                Done — refresh appointments
              </button>
              <button type="button" className="schd-link text-sm" onClick={closeEmbed}>
                Close
              </button>
            </div>
          </div>
          <CalcomEmbed mapping={activeMapping} />
          <p className="mt-3 text-center text-sm">
            <a className="schd-link" href={activeMapping.fallbackUrl} target="_blank" rel="noreferrer">
              Trouble loading? Open in a new tab
            </a>
          </p>
        </section>
      )}
    </div>
  );
}

// Lets staff satisfy a document request themselves (e.g. something collected in person or by
// mail) instead of waiting on the applicant to submit it through their portal. Mirrors the
// applicant's own two-step upload (start an intent, then PUT the bytes), but the request lands
// directly on 'accepted' -- the staff member uploading it is the reviewer, so there's no
// separate review step to wait on.
async function uploadDocumentAsStaff(applicationId: string, requestId: string, file: File) {
  const intent = await api<{
    uploadUrl: string;
    requiredHeaders: Record<string, string>;
  }>(`/applications/${applicationId}/document-requests/${requestId}/staff-uploads`, {
    method: 'POST',
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      contentLength: file.size,
    }),
  });
  const uploaded = await fetch(intent.uploadUrl, {
    method: 'PUT',
    headers: intent.requiredHeaders,
    body: file,
  });
  if (!uploaded.ok)
    throw new ApiError('UPLOAD_FAILED', "We couldn't upload this document. Please try again.");
}

function DocumentManager({ applicationId }: { applicationId: string }) {
  const client = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [viewingRequest, setViewingRequest] = useState<DocumentRequest | null>(null);
  const [editingRequest, setEditingRequest] = useState<DocumentRequest | null>(null);
  const [reviewingRequest, setReviewingRequest] = useState<DocumentRequest | null>(null);
  const [deletingRequest, setDeletingRequest] = useState<DocumentRequest | null>(null);
  const [filter, setFilter] = useState<
    'all' | 'in_progress' | 'needs_review' | 'in_revision' | 'approved'
  >('all');
  const requests = useQuery({
    queryKey: ['document-requests', applicationId],
    queryFn: () =>
      api<{ data: DocumentRequest[] }>(`/applications/${applicationId}/document-requests`),
  });
  const refresh = () =>
    void client.invalidateQueries({ queryKey: ['document-requests', applicationId] });

  const all = requests.data?.data ?? [];
  const isInRevision = (r: DocumentRequest) =>
    r.status === 'replacement_requested' || r.status === 'rejected';
  const counts = {
    all: all.length,
    in_progress: all.filter((r) => r.status === 'pending').length,
    needs_review: all.filter((r) => r.status === 'uploaded' || r.status === 'under_review').length,
    in_revision: all.filter(isInRevision).length,
    approved: all.filter((r) => r.status === 'accepted').length,
  };
  const FILTER_LABELS: Record<typeof filter, string> = {
    all: 'All',
    in_progress: 'In Progress',
    needs_review: 'Needs Review',
    in_revision: 'In Revision',
    approved: 'Approved',
  };
  const visible = all.filter((r) => {
    if (filter === 'in_progress') return r.status === 'pending';
    if (filter === 'needs_review') return r.status === 'uploaded' || r.status === 'under_review';
    if (filter === 'in_revision') return isInRevision(r);
    if (filter === 'approved') return r.status === 'accepted';
    return true;
  });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div
          className="flex flex-wrap rounded-control border border-border p-0.5"
          role="group"
          aria-label="Filter by status"
        >
          {(['all', 'in_progress', 'needs_review', 'in_revision', 'approved'] as const).map(
            (value) => (
              <button
                key={value}
                type="button"
                aria-current={filter === value ? 'true' : undefined}
                onClick={() => setFilter(value)}
                className={`min-h-9 whitespace-nowrap rounded-control px-3 text-sm font-semibold ${
                  filter === value ? 'bg-burgundy text-white' : 'text-ink/65 hover:text-ink'
                }`}
              >
                {FILTER_LABELS[value]} ({counts[value]})
              </button>
            ),
          )}
        </div>
        <button
          type="button"
          className="schd-button-primary shrink-0"
          onClick={() => setCreating(true)}
        >
          <FileUp className="size-5" /> Request document
        </button>
      </div>
      <section className="overflow-hidden rounded-card border border-border bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-fixed text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-muted text-xs font-semibold uppercase tracking-wide text-ink/60">
                <th className="w-[26%] px-4 py-3 font-semibold">Document name</th>
                <th className="w-[13%] px-4 py-3 font-semibold">Status</th>
                <th className="w-[16%] px-4 py-3 font-semibold">Submitted</th>
                <th className="w-[15%] px-4 py-3 font-semibold">Upload document</th>
                <th className="w-[30%] px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {requests.isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-ink/60">
                    Loading document requests…
                  </td>
                </tr>
              ) : requests.isError ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6">
                    <div className="schd-error text-sm" role="alert">
                      {errorMessage(requests.error)}{' '}
                      <button className="underline" onClick={() => void requests.refetch()}>
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-ink/60">
                    {all.length === 0
                      ? 'No document requests yet. Request one when the application needs a supporting file.'
                      : 'No requests match this filter.'}
                  </td>
                </tr>
              ) : (
                visible.map((request) => (
                  <DocumentRequestTableRow
                    key={request.id}
                    applicationId={applicationId}
                    request={request}
                    onView={() => setViewingRequest(request)}
                    onEdit={() => setEditingRequest(request)}
                    onReview={() => setReviewingRequest(request)}
                    onDelete={() => setDeletingRequest(request)}
                    onUploaded={refresh}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      {creating && (
        <DocumentRequestModal
          applicationId={applicationId}
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {viewingRequest && (
        <DocumentDetailsModal
          applicationId={applicationId}
          request={viewingRequest}
          onClose={() => setViewingRequest(null)}
        />
      )}
      {editingRequest && (
        <DocumentEditModal
          applicationId={applicationId}
          request={editingRequest}
          onCancel={() => setEditingRequest(null)}
          onSaved={() => {
            setEditingRequest(null);
            refresh();
          }}
        />
      )}
      {reviewingRequest && (
        <DocumentReviewModal
          applicationId={applicationId}
          request={reviewingRequest}
          onCancel={() => setReviewingRequest(null)}
          onSaved={() => {
            setReviewingRequest(null);
            refresh();
          }}
        />
      )}
      {deletingRequest && (
        <DocumentDeleteModal
          applicationId={applicationId}
          request={deletingRequest}
          onCancel={() => setDeletingRequest(null)}
          onDeleted={() => {
            setDeletingRequest(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function DocumentRequestTableRow({
  applicationId,
  request,
  onView,
  onEdit,
  onReview,
  onDelete,
  onUploaded,
}: {
  applicationId: string;
  request: DocumentRequest;
  onView: () => void;
  onEdit: () => void;
  onReview: () => void;
  onDelete: () => void;
  onUploaded: () => void;
}) {
  const canEdit = request.status === 'pending';
  const canDelete = !['accepted', 'waived'].includes(request.status);
  const canReview = request.status === 'uploaded' || request.status === 'under_review';
  // Staff can provide a document on the applicant's behalf any time the request isn't already
  // resolved -- including re-uploading over a rejected/replacement-requested submission.
  const canUpload = !['accepted', 'waived', 'cancelled'].includes(request.status);
  const latestDocument = request.documents[0] ?? null;
  const canDownload = Boolean(
    latestDocument && DOWNLOADABLE_DOCUMENT_STATUSES.has(latestDocument.status),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await uploadDocumentAsStaff(applicationId, request.id, file);
      toast.success('Document uploaded and marked approved.');
      onUploaded();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setUploading(false);
    }
  };
  return (
    <tr>
      <td className="px-4 py-4">
        <p className="min-w-0 font-semibold">{request.title}</p>
      </td>
      <td className="px-4 py-4">
        <StatusBadge tone={documentRequestStatusTone(request.status)}>
          {documentRequestStatusLabel(request.status)}
        </StatusBadge>
      </td>
      <td className="px-4 py-4 text-ink/70">
        {latestDocument?.uploadedAt
          ? new Date(latestDocument.uploadedAt).toLocaleDateString()
          : 'Not submitted yet'}
      </td>
      <td className="px-4 py-4">
        {canUpload ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(event) => void handleFile(event)}
            />
            <button
              type="button"
              className="schd-button-secondary-sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="size-4" aria-hidden="true" />
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </>
        ) : (
          <span className="text-sm text-ink/50">—</span>
        )}
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center justify-end gap-2">
          {canReview && (
            <button type="button" className="schd-button-secondary-sm" onClick={onReview}>
              Review
            </button>
          )}
          {canDownload && latestDocument && (
            <a
              href={`${apiBaseUrl}/applications/${applicationId}/documents/${latestDocument.id}/download`}
              aria-label={`Download ${latestDocument.fileName}`}
              className="grid size-9 shrink-0 place-items-center rounded-control border border-border text-ink/60 hover:bg-surface-muted hover:text-ink"
            >
              <Download className="size-4" aria-hidden="true" />
            </a>
          )}
          <ActionMenu
            label="More actions"
            items={[
              { label: 'View details', onClick: onView },
              ...(canEdit ? [{ label: 'Edit', onClick: onEdit }] : []),
              ...(canDelete
                ? [{ label: 'Delete request', destructive: true, onClick: onDelete }]
                : []),
            ]}
          />
        </div>
      </td>
    </tr>
  );
}

function DocumentDetailsModal({
  applicationId,
  request,
  onClose,
}: {
  applicationId: string;
  request: DocumentRequest;
  onClose: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onClose);
  const dialogRef = useDialogFocus(requestClose);
  const submittedDocuments = request.documents.filter((d) => d.status !== 'withdrawn');
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-details-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="document-details-title" className="font-display text-xl">
            {request.title}
          </h2>
          <StatusBadge tone={documentRequestStatusTone(request.status)}>
            {documentRequestStatusLabel(request.status)}
          </StatusBadge>
        </div>
        <dl className="mt-4 space-y-4 text-sm">
          {request.instructions && (
            <div>
              <dt className="font-semibold text-ink/70">Instructions for applicant</dt>
              <dd className="mt-0.5 text-ink/85">{request.instructions}</dd>
            </div>
          )}
          <div>
            <dt className="font-semibold text-ink/70">Due date</dt>
            <dd className="mt-0.5 text-ink/85">
              {request.dueAt ? new Date(request.dueAt).toLocaleDateString() : 'No due date set'}
            </dd>
          </div>
          {submittedDocuments.length > 0 && (
            <div>
              <dt className="font-semibold text-ink/70">
                Submitted file{submittedDocuments.length > 1 ? 's' : ''}
              </dt>
              <dd className="mt-1 space-y-1.5">
                {submittedDocuments.map((document) => {
                  const downloadable = DOWNLOADABLE_DOCUMENT_STATUSES.has(document.status);
                  return (
                    <div key={document.id} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 break-all text-ink/85">{document.fileName}</span>
                      {downloadable ? (
                        <a
                          href={`${apiBaseUrl}/applications/${applicationId}/documents/${document.id}/download`}
                          aria-label={`Download ${document.fileName}`}
                          className="grid size-8 shrink-0 place-items-center rounded-control border border-border text-ink/60 hover:bg-surface-muted hover:text-ink"
                        >
                          <Download className="size-4" aria-hidden="true" />
                        </a>
                      ) : (
                        <span className="shrink-0 text-xs text-ink/50">not available</span>
                      )}
                    </div>
                  );
                })}
              </dd>
            </div>
          )}
          {request.lastReason && (
            <div>
              <dt className="font-semibold text-ink/70">Latest reviewer comment</dt>
              <dd className="mt-0.5 text-ink/85">&ldquo;{request.lastReason}&rdquo;</dd>
            </div>
          )}
        </dl>
        <div className="mt-5 flex justify-end">
          <button type="button" className="schd-button-secondary" onClick={requestClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentRequestModal({
  applicationId,
  onCancel,
  onCreated,
}: {
  applicationId: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const title = String(values.get('title') || '').trim();
    const instructions = String(values.get('instructions') || '').trim();
    const dueAtRaw = String(values.get('dueAt') || '');
    setSaving(true);
    try {
      await api(`/applications/${applicationId}/document-requests`, {
        method: 'POST',
        body: JSON.stringify({
          customTitle: { en: title, es: title },
          customInstructions: { en: instructions || null, es: instructions || null },
          dueAt: dueAtRaw ? new Date(dueAtRaw).toISOString() : null,
        }),
      });
      toast.success('Document request created.');
      onCreated();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-document-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="request-document-title" className="font-display text-xl">
          Request document from applicant
        </h2>
        <form onSubmit={submit} className="mt-4">
          <label className="block text-sm font-semibold">
            Request title (required)
            <input
              required
              autoFocus
              name="title"
              maxLength={300}
              className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
            />
          </label>
          <label className="mt-4 block text-sm font-semibold">
            Due date (optional)
            <input
              type="datetime-local"
              name="dueAt"
              className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
            />
          </label>
          <label className="mt-4 block text-sm font-semibold">
            Instructions for applicant (optional)
            <textarea
              name="instructions"
              rows={3}
              className="mt-1 block w-full rounded-control border bg-white px-2 py-1 font-normal"
            />
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Cancel
            </button>
            <button disabled={saving} className="schd-button-primary">
              {saving ? 'Requesting…' : 'Request document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DocumentEditModal({
  applicationId,
  request,
  onCancel,
  onSaved,
}: {
  applicationId: string;
  request: DocumentRequest;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setSaving(true);
    try {
      const body: Record<string, unknown> = {};
      if (request.mode === 'custom') {
        const nextTitle = String(values.get('title') || '').trim();
        const nextInstructions = String(values.get('instructions') || '').trim();
        body.customTitle = { en: nextTitle, es: nextTitle };
        body.customInstructions = { en: nextInstructions || null, es: nextInstructions || null };
      }
      const dueAtRaw = String(values.get('dueAt') || '');
      body.dueAt = dueAtRaw ? new Date(dueAtRaw).toISOString() : null;
      await api(`/applications/${applicationId}/document-requests/${request.id}/details`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      toast.success('Document request updated.');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-document-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="edit-document-title" className="font-display text-xl">
          Edit document request
        </h2>
        <form onSubmit={submit} className="mt-4">
          {request.mode === 'custom' && (
            <>
              <label className="block text-sm font-semibold">
                Title (required)
                <input
                  required
                  autoFocus
                  name="title"
                  defaultValue={request.title}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
                />
              </label>
              <label className="mt-4 block text-sm font-semibold">
                Applicant instructions
                <textarea
                  name="instructions"
                  defaultValue={request.instructions ?? ''}
                  rows={3}
                  className="mt-1 block w-full rounded-control border bg-white px-2 py-1 font-normal"
                />
              </label>
            </>
          )}
          <label className={`block text-sm font-semibold ${request.mode === 'custom' ? 'mt-4' : ''}`}>
            Due date
            <input
              type="datetime-local"
              name="dueAt"
              defaultValue={request.dueAt ? request.dueAt.slice(0, 16) : ''}
              className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
            />
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Cancel
            </button>
            <button disabled={saving} className="schd-button-primary">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DocumentReviewModal({
  applicationId,
  request,
  onCancel,
  onSaved,
}: {
  applicationId: string;
  request: DocumentRequest;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!reason.trim()) return;
    setSaving(true);
    try {
      await api(`/applications/${applicationId}/document-requests/${request.id}`, {
        method: 'PATCH',
        // notesToApplicant carries the same text into the applicant's own view of this request --
        // `reason` alone only lands in the staff-only audit trail.
        body: JSON.stringify({
          status: 'accepted',
          reason: reason.trim(),
          notesToApplicant: reason.trim(),
        }),
      });
      toast.success('Document accepted.');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-document-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="review-document-title" className="font-display text-xl">
          Review {request.title}
        </h2>
        {request.documents.filter((d) => ['available', 'accepted'].includes(d.status)).length >
          0 && (
          <ul className="mt-1 space-y-1">
            {request.documents
              .filter((d) => ['available', 'accepted'].includes(d.status))
              .map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="min-w-0 break-all text-ink/65">{d.fileName}</span>
                  <a
                    href={`${apiBaseUrl}/applications/${applicationId}/documents/${d.id}/download`}
                    aria-label={`Download ${d.fileName}`}
                    className="grid size-7 shrink-0 place-items-center rounded-control border border-border text-ink/60 hover:bg-surface-muted hover:text-ink"
                  >
                    <Download className="size-3.5" aria-hidden="true" />
                  </a>
                </li>
              ))}
          </ul>
        )}
        <form onSubmit={submit} className="mt-4">
          <label className="block text-sm font-semibold">
            Note (required)
            <textarea
              autoFocus
              required
              maxLength={2000}
              rows={4}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="mt-1 block w-full rounded-control border bg-white px-2 py-1 font-normal"
            />
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Close
            </button>
            <button disabled={saving} className="schd-button-primary">
              {saving ? 'Saving…' : 'Confirm acceptance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DocumentDeleteModal({
  applicationId,
  request,
  onCancel,
  onDeleted,
}: {
  applicationId: string;
  request: DocumentRequest;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api(`/applications/${applicationId}/document-requests/${request.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      toast.success('Document request deleted.');
      onDeleted();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-document-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card w-full max-w-md`}
      >
        <h2 id="delete-document-title" className="font-display text-xl">
          Delete document request
        </h2>
        <p className="mt-2 text-sm text-ink/70">
          "{request.title}" — this will be permanently removed from both the staff and applicant
          views. This cannot be undone.
        </p>
        <form onSubmit={submit} className="mt-4">
          <label className="block text-sm font-semibold">
            Reason (optional)
            <textarea
              autoFocus
              maxLength={2000}
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="mt-1 block w-full rounded-control border bg-white px-2 py-1 font-normal"
            />
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Keep request
            </button>
            <button disabled={saving} className="schd-button-destructive-strong">
              {saving ? 'Deleting…' : 'Confirm deletion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SharedResourceManager({ applicationId }: { applicationId: string }) {
  const client = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [deletingResource, setDeletingResource] = useState<SharedResource | null>(null);
  const resources = useQuery({
    queryKey: ['shared-resources', applicationId],
    queryFn: () =>
      api<{ data: SharedResource[] }>(`/applications/${applicationId}/shared-resources`),
  });
  const refresh = () =>
    void client.invalidateQueries({ queryKey: ['shared-resources', applicationId] });
  const all = resources.data?.data ?? [];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg">Shared resources</h2>
          <p className="text-sm text-ink/65">
            Files you upload here are visible to the applicant in their portal.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            className="schd-button-secondary"
            onClick={() => setAssigning(true)}
          >
            <BookOpen className="size-5" /> Assign shared resource
          </button>
          <button
            type="button"
            className="schd-button-primary"
            onClick={() => setUploading(true)}
          >
            <FileUp className="size-5" /> Upload file
          </button>
        </div>
      </div>
      <section className="overflow-hidden rounded-card border border-border bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] table-fixed text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-muted text-xs font-semibold uppercase tracking-wide text-ink/60">
                <th className="w-[40%] px-4 py-3 font-semibold">File</th>
                <th className="w-[15%] px-4 py-3 font-semibold">Size</th>
                <th className="w-[25%] px-4 py-3 font-semibold">Uploaded</th>
                <th className="w-[20%] px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {resources.isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-ink/60">
                    Loading shared resources…
                  </td>
                </tr>
              ) : resources.isError ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6">
                    <div className="schd-error text-sm" role="alert">
                      {errorMessage(resources.error)}{' '}
                      <button className="underline" onClick={() => void resources.refetch()}>
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : all.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-ink/60">
                    No files shared yet. Upload one for the applicant to view or download.
                  </td>
                </tr>
              ) : (
                all.map((resource) => (
                  <tr key={resource.id}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="size-4 shrink-0 text-ink/40" aria-hidden="true" />
                        <div className="min-w-0">
                          <p className="truncate font-semibold">{resource.title}</p>
                          <p className="truncate text-xs text-ink/55">{resource.fileName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink/70">
                      {(resource.contentLength / 1_000_000).toFixed(1)} MB
                    </td>
                    <td className="px-4 py-3 text-ink/70">
                      {new Date(resource.uploadedAt).toLocaleDateString()}
                      {resource.uploadedBy && (
                        <span className="block text-xs text-ink/50">
                          by {resource.uploadedBy.name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-3">
                        <a
                          href={`${apiBaseUrl}/applications/${applicationId}/shared-resources/${resource.id}/download`}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-burgundy hover:underline"
                        >
                          <Download className="size-4" aria-hidden="true" /> Download
                        </a>
                        <ActionMenu
                          label="More actions"
                          items={[
                            {
                              label: 'Delete',
                              destructive: true,
                              onClick: () => setDeletingResource(resource),
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      {uploading && (
        <SharedResourceUploadModal
          applicationId={applicationId}
          onCancel={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            refresh();
          }}
        />
      )}
      {assigning && (
        <AssignSharedResourceModal
          applicationId={applicationId}
          onCancel={() => setAssigning(false)}
          onAssigned={() => {
            setAssigning(false);
            refresh();
          }}
        />
      )}
      {deletingResource && (
        <SharedResourceDeleteModal
          applicationId={applicationId}
          resource={deletingResource}
          onCancel={() => setDeletingResource(null)}
          onDeleted={() => {
            setDeletingResource(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

const sharedResourceAccept = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
const sharedResourceMimeTypes = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

function SharedResourceDropzone({
  file,
  onFileSelected,
}: {
  file: File | null;
  onFileSelected: (file: File) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFiles = (files: FileList | null) => {
    const picked = files?.[0];
    if (!picked) return;
    if (!sharedResourceMimeTypes.has(picked.type)) {
      toast.error('This file type is not supported.');
      return;
    }
    onFileSelected(picked);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        handleFiles(event.dataTransfer.files);
      }}
      className={`flex cursor-pointer flex-col items-center gap-2 rounded-control border-2 border-dashed p-6 text-center transition-colors ${
        dragging ? 'border-burgundy bg-burgundy/5' : 'border-border hover:border-burgundy/50'
      }`}
    >
      <FileUp className="size-6 text-ink/40" aria-hidden="true" />
      {file ? (
        <p className="text-sm font-semibold text-ink">{file.name}</p>
      ) : (
        <p className="text-sm text-ink/70">
          Drag and drop a file here, or <span className="font-semibold text-burgundy">browse files</span>
        </p>
      )}
      <p className="text-xs text-ink/55">PDF, image, Word, Excel, or PowerPoint · max 10 MB</p>
      <input
        ref={inputRef}
        type="file"
        accept={sharedResourceAccept}
        className="sr-only"
        onChange={(event) => handleFiles(event.target.files)}
      />
    </div>
  );
}

function SharedResourceUploadModal({
  applicationId,
  onCancel,
  onUploaded,
}: {
  applicationId: string;
  onCancel: () => void;
  onUploaded: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    if (!file) {
      toast.error('Choose a file to upload.');
      return;
    }
    values.set('file', file);
    setUploading(true);
    try {
      await api(`/applications/${applicationId}/shared-resources`, {
        method: 'POST',
        body: values,
      });
      toast.success('File shared with the applicant.');
      onUploaded();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-shared-resource-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="upload-shared-resource-title" className="font-display text-xl">
          Upload a file to share
        </h2>
        <p className="mt-2 text-sm text-ink/65">
          The applicant will be able to view and download this from their portal.
        </p>
        <form onSubmit={submit} className="mt-4">
          <label className="block text-sm font-semibold">
            Title (optional)
            <input
              autoFocus
              name="title"
              maxLength={300}
              placeholder="Defaults to the file name"
              className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
            />
          </label>
          <div className="mt-4">
            <span className="block text-sm font-semibold">File (required)</span>
            <div className="mt-1">
              <SharedResourceDropzone file={file} onFileSelected={setFile} />
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Cancel
            </button>
            <button disabled={uploading} className="schd-button-primary">
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SharedResourceDeleteModal({
  applicationId,
  resource,
  onCancel,
  onDeleted,
}: {
  applicationId: string;
  resource: SharedResource;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [deleting, setDeleting] = useState(false);
  const confirm = async () => {
    setDeleting(true);
    try {
      await api(`/applications/${applicationId}/shared-resources/${resource.id}`, {
        method: 'DELETE',
      });
      toast.success('Shared file removed.');
      onDeleted();
    } catch (error) {
      toast.error(errorMessage(error));
      setDeleting(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-shared-resource-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card w-full max-w-md`}
      >
        <h2 id="delete-shared-resource-title" className="font-display text-xl">
          Remove shared file
        </h2>
        <p className="mt-2 text-sm text-ink/70">
          "{resource.title}" will be removed from the applicant's portal immediately and can't be
          undone.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="schd-button-secondary" onClick={requestClose}>
            Keep file
          </button>
          <button disabled={deleting} onClick={() => void confirm()} className="schd-button-destructive-strong">
            {deleting ? 'Removing…' : 'Confirm removal'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignSharedResourceModal({
  applicationId,
  onCancel,
  onAssigned,
}: {
  applicationId: string;
  onCancel: () => void;
  onAssigned: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assigning, setAssigning] = useState(false);
  const library = useQuery({
    queryKey: ['settings', 'materials'],
    queryFn: () => api<{ data: StudyMaterial[] }>('/settings/study-materials'),
  });
  const available = (library.data?.data ?? []).filter((m) => m.active);
  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const submit = async () => {
    if (selected.size === 0) {
      toast.error('Choose at least one resource to assign.');
      return;
    }
    setAssigning(true);
    try {
      await api(`/applications/${applicationId}/shared-resources/assign`, {
        method: 'POST',
        body: JSON.stringify({ studyMaterialIds: [...selected] }),
      });
      toast.success(
        selected.size === 1 ? 'Resource assigned to the applicant.' : 'Resources assigned to the applicant.',
      );
      onAssigned();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setAssigning(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-shared-resource-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="assign-shared-resource-title" className="font-display text-xl">
          Assign shared resource
        </h2>
        <p className="mt-2 text-sm text-ink/65">
          Pick one or more resources from the library. Each becomes a copy the applicant can view
          in their Shared resources section.
        </p>
        <div className="mt-4">
          {library.isLoading ? (
            <div className="space-y-2" role="status">
              <span className="sr-only">Loading resource library</span>
              <div className="schd-skeleton h-10 w-full" aria-hidden="true" />
              <div className="schd-skeleton h-10 w-full" aria-hidden="true" />
              <div className="schd-skeleton h-10 w-full" aria-hidden="true" />
            </div>
          ) : library.isError ? (
            <ErrorRetry query={library} />
          ) : available.length === 0 ? (
            <p className="rounded-card border border-dashed border-border bg-surface p-4 text-center text-sm text-ink/65">
              No shared resources have been uploaded yet. Upload one from the Shared Resources tab
              first.
            </p>
          ) : (
            <fieldset className="max-h-72 space-y-1 overflow-y-auto">
              <legend className="sr-only">Resources to assign</legend>
              {available.map((material) => (
                <label
                  key={material.id}
                  className="flex items-center gap-3 rounded-control border border-border px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(material.id)}
                    onChange={() => toggle(material.id)}
                  />
                  <span className="min-w-0 flex-1 truncate font-semibold">{material.title}</span>
                  <span className="shrink-0 text-xs font-semibold text-ink/55">
                    {material.language === 'es' ? 'Español' : 'English'}
                  </span>
                </label>
              ))}
            </fieldset>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="schd-button-secondary" onClick={requestClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={assigning || available.length === 0}
            onClick={() => void submit()}
            className="schd-button-primary"
          >
            {assigning
              ? 'Assigning…'
              : selected.size > 0
                ? `Assign (${selected.size})`
                : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Quieter than schd-link (no underline, no bold burgundy) so a page with several of these in a
// row -- e.g. every Overview widget's footer -- doesn't read as four shouting calls to action.
function WidgetLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      className="group mt-3 inline-flex items-center gap-1 text-sm font-semibold text-ink/65 hover:text-burgundy"
      onClick={onClick}
    >
      {children}
      <ArrowRight
        className="size-3.5 transition-transform group-hover:translate-x-0.5"
        aria-hidden="true"
      />
    </button>
  );
}

function WidgetCard({
  icon: Icon,
  title,
  action,
  className = '',
  children,
}: {
  icon: typeof Users;
  title: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`rounded-card border bg-surface p-4 sm:p-5 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="size-5 text-burgundy" aria-hidden="true" />
          <h3 className="font-semibold">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function PipelineProgressWidget({
  application,
  onNavigate,
}: {
  application: Application;
  onNavigate: (tab: ApplicantTab) => void;
}) {
  const stages = useQuery({
    queryKey: ['workflow-stages'],
    queryFn: () => api<{ data: Stage[] }>('/settings/workflow-stages'),
    staleTime: 5 * 60_000,
  });
  const currentOrder = application.currentStage.sortOrder ?? 0;
  const allStages = stages.data?.data ?? [];
  const stepNumber = allStages.filter((s) => s.sortOrder <= currentOrder).length;
  const totalSteps = allStages.length;
  const percent = totalSteps > 0 ? Math.round((stepNumber / totalSteps) * 100) : 0;
  const nextStage = allStages.find((s) => s.sortOrder === currentOrder + 1);
  return (
    <section className="rounded-card border border-burgundy/20 bg-surface-muted p-4 sm:p-5 lg:col-span-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Target className="size-5 text-burgundy" aria-hidden="true" />
          <p className="font-semibold">
            Step {stepNumber} of {totalSteps} — &quot;{application.currentStage.name}&quot;
          </p>
        </div>
        <p className="text-sm text-ink/65">
          {nextStage ? `Next: ${nextStage.translations.en.name}` : 'Final stage'}
        </p>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div
          className="h-2.5 flex-1 overflow-hidden rounded-full bg-border"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="h-full rounded-full bg-burgundy" style={{ width: `${percent}%` }} />
        </div>
        <span className="shrink-0 text-sm font-semibold text-ink/70">{percent}% Complete</span>
        <button
          type="button"
          className="schd-button-secondary-sm shrink-0"
          onClick={() => onNavigate('status')}
        >
          Advance Stage <ArrowRight className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

function RequiredDocsWidget({
  applicationId,
  onNavigate,
}: {
  applicationId: string;
  onNavigate: (tab: ApplicantTab) => void;
}) {
  const requests = useQuery({
    queryKey: ['document-requests', applicationId],
    queryFn: () => api<{ data: DocumentRequest[] }>(`/applications/${applicationId}/document-requests`),
  });
  const all = requests.data?.data ?? [];
  const approved = all.filter((r) => r.status === 'accepted').length;
  return (
    <WidgetCard icon={FileText} title={`Documents (${approved} of ${all.length})`} className="lg:col-span-4">
      {requests.isLoading ? (
        <p className="mt-2 text-sm" role="status">
          Loading…
        </p>
      ) : requests.isError ? (
        <p className="mt-2 text-sm text-ink/65">Couldn&apos;t load documents.</p>
      ) : all.length === 0 ? (
        <p className="mt-2 text-sm text-ink/65">No documents requested yet.</p>
      ) : (
        <ul className="mt-3 space-y-2 text-sm">
          {all.slice(0, 3).map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate">{r.title}</span>
              <StatusBadge tone={documentRequestStatusTone(r.status)}>
                {documentRequestStatusLabel(r.status)}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}
      <WidgetLink onClick={() => onNavigate('documents')}>View all documents</WidgetLink>
    </WidgetCard>
  );
}

function AppointmentWidget({
  applicationId,
  onNavigate,
}: {
  applicationId: string;
  onNavigate: (tab: ApplicantTab) => void;
}) {
  const appointments = useQuery({
    queryKey: ['application-appointments', applicationId],
    queryFn: () => api<{ data: Appointment[] }>(`/applications/${applicationId}/appointments`),
  });
  const all = appointments.data?.data ?? [];
  const upcoming = all
    .filter((a) => !['cancelled', 'completed', 'no_show', 'rejected'].includes(a.status))
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())[0];
  return (
    <WidgetCard icon={CalendarClock} title="Appointments" className="lg:col-span-4">
      {appointments.isLoading ? (
        <p className="mt-2 text-sm" role="status">
          Loading…
        </p>
      ) : appointments.isError ? (
        <p className="mt-2 text-sm text-ink/65">Couldn&apos;t load appointments.</p>
      ) : upcoming ? (
        <div className="mt-2 text-sm">
          <p className="font-semibold">{upcoming.typeName}</p>
          <p className="mt-0.5 text-ink/65">{new Date(upcoming.startsAt).toLocaleString()}</p>
          <StatusBadge tone={appointmentStatusTone(upcoming.status)}>
            {appointmentStatusLabel(upcoming.status)}
          </StatusBadge>
        </div>
      ) : (
        <p className="mt-2 text-sm text-ink/65">No appointments scheduled.</p>
      )}
      <WidgetLink onClick={() => onNavigate('appointments')}>Manage appointments</WidgetLink>
    </WidgetCard>
  );
}

function PortalAccessWidget({ applicant, onSaved }: { applicant: Applicant; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const act = async (path: string, method: string, successMessage: string) => {
    setBusy(true);
    try {
      await api(path, { method });
      toast.success(successMessage);
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  const claimed = Boolean(applicant.portalAccount);
  return (
    <WidgetCard icon={Mail} title="Portal Access" className="lg:col-span-4">
      <div className="mt-2 space-y-1 text-sm">
        <p>
          Status:{' '}
          <StatusBadge tone={claimed ? staffStatusTone(applicant.portalAccount!.status) : 'attention'}>
            {claimed ? applicant.portalAccount!.status : 'Not claimed'}
          </StatusBadge>
        </p>
        {claimed && applicant.portalAccount!.lastLoginAt && (
          <p className="text-ink/65">
            Last login: {new Date(applicant.portalAccount!.lastLoginAt).toLocaleString()}
          </p>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          className="schd-button-secondary-sm"
          onClick={() => void act(`/applicants/${applicant.id}/invite`, 'POST', 'Invitation sent.')}
        >
          <Send className="size-4" aria-hidden="true" /> Magic link
        </button>
        <button
          type="button"
          disabled={busy}
          className="schd-button-destructive-sm"
          onClick={() => setConfirmRevoke(true)}
        >
          Revoke
        </button>
      </div>
      {confirmRevoke && (
        <div className="mt-3 rounded-control border border-red-200 bg-red-50 p-2.5 text-sm text-red-900">
          <p>Revoke this invitation? The applicant won&apos;t be able to sign in until you send a new one.</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={busy}
              className="schd-button-destructive-sm"
              onClick={() => {
                setConfirmRevoke(false);
                void act(`/applicants/${applicant.id}/invite`, 'DELETE', 'Invitation revoked.');
              }}
            >
              Revoke invitation
            </button>
            <button
              type="button"
              className="min-h-9 px-2 text-sm underline"
              onClick={() => setConfirmRevoke(false)}
            >
              Keep invitation
            </button>
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

function AssignedStaffWidget({
  application,
  onNavigate,
}: {
  application: Application;
  onNavigate: (tab: ApplicantTab) => void;
}) {
  const activeAssignments = application.assignments.filter((item) => !item.unassignedAt);
  return (
    <WidgetCard icon={Users} title={`Assigned Staff (${activeAssignments.length})`} className="lg:col-span-6">
      {activeAssignments.length ? (
        <ul className="mt-2 space-y-2 text-sm">
          {activeAssignments.map((item) => (
            <li key={item.id} className="flex items-center gap-2">
              <span className="grid size-7 shrink-0 place-items-center rounded-full bg-burgundy text-[11px] font-bold text-white">
                {initialsFromName(item.assignedStaff.name)}
              </span>
              <span className="min-w-0 truncate">
                {item.assignedStaff.name}{' '}
                <span className="text-ink/55">({item.assignedStaff.email})</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-ink/65">No staff assigned yet.</p>
      )}
      <WidgetLink onClick={() => onNavigate('team')}>Manage team</WidgetLink>
    </WidgetCard>
  );
}

function RecentNotesWidget({
  applicationId,
  onNavigate,
}: {
  applicationId: string;
  onNavigate: (tab: ApplicantTab) => void;
}) {
  const notes = useQuery({
    queryKey: ['notes', applicationId],
    queryFn: () =>
      api<{
        data: Array<{ id: string; body: string; createdAt: string; createdBy: { name: string } | null }>;
      }>(`/applications/${applicationId}/notes`),
  });
  const recent = (notes.data?.data ?? []).slice(0, 2);
  return (
    <WidgetCard
      icon={StickyNote}
      title="Internal Notes"
      action={<StatusBadge tone="neutral">Staff only</StatusBadge>}
      className="lg:col-span-6"
    >
      {notes.isLoading ? (
        <p className="mt-2 text-sm" role="status">
          Loading…
        </p>
      ) : recent.length ? (
        <ul className="mt-2 space-y-2 text-sm">
          {recent.map((item) => (
            <li key={item.id}>
              <p className="font-semibold">
                {item.createdBy?.name ?? 'Former user'}
                <span className="ml-1.5 font-normal text-ink/55">
                  · {new Date(item.createdAt).toLocaleDateString()}
                </span>
              </p>
              <p className="mt-0.5 truncate text-ink/70">&quot;{item.body}&quot;</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-ink/65">No internal notes yet.</p>
      )}
      <WidgetLink onClick={() => onNavigate('team')}>View all notes</WidgetLink>
    </WidgetCard>
  );
}

function OverviewDashboard({
  applicant,
  onRefresh,
  onNavigate,
}: {
  applicant: Applicant;
  onRefresh: () => void;
  onNavigate: (tab: ApplicantTab) => void;
}) {
  if (!applicant.activeApplication) {
    return (
      <div className="grid gap-5">
        <PortalAccessWidget applicant={applicant} onSaved={onRefresh} />
        <p className="rounded-card border border-dashed border-border bg-surface p-6 text-center text-sm text-ink/65">
          No active application. Status, documents, appointments, and team assignment become
          available once one is created.
        </p>
      </div>
    );
  }
  const application = applicant.activeApplication;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      <PipelineProgressWidget application={application} onNavigate={onNavigate} />
      <RequiredDocsWidget applicationId={application.id} onNavigate={onNavigate} />
      <AppointmentWidget applicationId={application.id} onNavigate={onNavigate} />
      <PortalAccessWidget applicant={applicant} onSaved={onRefresh} />
      <AssignedStaffWidget application={application} onNavigate={onNavigate} />
      <RecentNotesWidget applicationId={application.id} onNavigate={onNavigate} />
    </div>
  );
}

function ProfileEditModal({
  applicant,
  onCancel,
  onSaved,
}: {
  applicant: Applicant;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [saving, setSaving] = useState(false);
  const [language, setLanguage] = useState(applicant.preferredLanguage);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await api(`/applicants/${applicant.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: {
            firstName: values.get('firstName'),
            middleName: values.get('middleName') || null,
            lastName: values.get('lastName'),
          },
          email: values.get('email'),
          phone: values.get('phone') || null,
          preferredLanguage: language,
        }),
      });
      toast.success('Applicant profile updated.');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="edit-profile-title" className="font-display text-xl">
          Edit profile
        </h2>
        <form onSubmit={submit} className="mt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['firstName', 'First name (required)', applicant.name.firstName],
              ['middleName', 'Middle name (optional)', applicant.name.middleName ?? ''],
              ['lastName', 'Last name (required)', applicant.name.lastName],
              ['email', 'Email (required)', applicant.email],
              ['phone', 'Phone (optional)', applicant.phone ?? ''],
            ].map(([field, label, value], index) => (
              <label key={field} className="text-sm font-semibold">
                {label}
                <input
                  required={field !== 'middleName' && field !== 'phone'}
                  autoFocus={index === 0}
                  name={field}
                  defaultValue={value}
                  type={field === 'email' ? 'email' : 'text'}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
                />
              </label>
            ))}
            <label className="text-sm font-semibold">
              Preferred language
              <div className="mt-1">
                <SearchableSelect
                  value={language}
                  onChange={(next) => setLanguage(next as 'en' | 'es')}
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'es', label: 'Español' },
                  ]}
                />
              </div>
            </label>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Cancel
            </button>
            <button disabled={saving} className="schd-button-primary">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RemoveStaffModal({
  user,
  onCancel,
  onRemoved,
}: {
  user: StaffUser;
  onCancel: () => void;
  onRemoved: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [removing, setRemoving] = useState(false);
  const remove = async () => {
    setRemoving(true);
    try {
      await api(`/settings/users/${user.id}`, { method: 'DELETE' });
      toast.success('Staff member removed.');
      onRemoved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setRemoving(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-staff-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card w-full max-w-md`}
      >
        <h2 id="remove-staff-title" className="font-display text-xl">
          Remove {user.name.firstName} {user.name.lastName}?
        </h2>
        <p className="mt-3 text-sm text-ink/70">
          This immediately signs them out and revokes portal access. They'll stay listed on any
          applicants already assigned to them, but can no longer sign in or be assigned to new
          ones.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="schd-button-secondary" onClick={requestClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={removing}
            className="schd-button-destructive-strong"
            onClick={() => void remove()}
          >
            {removing ? 'Removing…' : 'Remove staff member'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StaffAccessRow({
  user,
  isSelf,
  onEdit,
  onRemove,
}: {
  user: StaffUser;
  isSelf: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const initials =
    `${user.name.firstName[0] ?? ''}${user.name.lastName[0] ?? ''}`.toUpperCase() || '?';
  return (
    <tr>
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-burgundy text-xs font-bold text-white">
            {initials}
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold">
              {user.name.firstName} {user.name.lastName}
            </p>
            <p className="truncate text-sm text-ink/65">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        <StatusBadge tone="neutral">{user.role === 'admin' ? 'Admin' : 'Staff'}</StatusBadge>
      </td>
      <td className="px-4 py-4">
        <StatusBadge tone={staffStatusTone(user.status)}>
          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
        </StatusBadge>
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="schd-button-secondary-sm" onClick={onEdit}>
            Edit
          </button>
          <ActionMenu
            label={`More actions for ${user.name.firstName} ${user.name.lastName}`}
            items={[
              {
                label: 'Remove',
                destructive: true,
                disabled: isSelf,
                onClick: onRemove,
              },
            ]}
          />
        </div>
      </td>
    </tr>
  );
}

function StaffInviteModal({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState('staff');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await api('/settings/users', {
        method: 'POST',
        body: JSON.stringify({
          email: values.get('email'),
          name: {
            firstName: values.get('firstName'),
            middleName: null,
            lastName: values.get('lastName'),
          },
          role,
          assignable: true,
        }),
      });
      toast.success('Invitation created. The account is pending until it is accepted.');
      onCreated();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-staff-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="invite-staff-title" className="font-display text-xl">
          Invite staff member
        </h2>
        <form onSubmit={submit} className="mt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-semibold">
              First name (required)
              <input
                required
                autoFocus
                name="firstName"
                maxLength={100}
                className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
              />
            </label>
            <label className="text-sm font-semibold">
              Last name (required)
              <input
                required
                name="lastName"
                maxLength={100}
                className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
              />
            </label>
            <label className="text-sm font-semibold sm:col-span-2">
              Email address (required)
              <input
                required
                type="email"
                name="email"
                className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
              />
            </label>
            <label className="text-sm font-semibold sm:col-span-2">
              Role
              <div className="mt-1 font-normal">
                <SearchableSelect
                  value={role}
                  onChange={setRole}
                  options={[
                    { value: 'staff', label: 'Staff' },
                    { value: 'admin', label: 'Admin' },
                  ]}
                />
              </div>
            </label>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Cancel
            </button>
            <button disabled={saving} className="schd-button-primary">
              {saving ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StaffEditModal({
  user,
  onCancel,
  onSaved,
}: {
  user: StaffUser;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string>(user.status === 'invited' ? 'active' : user.status);
  const [pendingValues, setPendingValues] = useState<FormData | null>(null);
  const applyUpdate = async (values: FormData) => {
    setSaving(true);
    try {
      await api(`/settings/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: {
            firstName: values.get('firstName'),
            middleName: user.name.middleName,
            lastName: values.get('lastName'),
          },
          status,
          assignable: true,
        }),
      });
      toast.success('Staff member updated.');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    // Reducing access (suspending/disabling someone who wasn't already) is consequential enough
    // to confirm; routine edits and reactivating someone go straight through.
    if (status !== 'active' && status !== user.status) {
      setPendingValues(values);
      return;
    }
    void applyUpdate(values);
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-staff-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="edit-staff-title" className="font-display text-xl">
          Edit {user.name.firstName} {user.name.lastName}
        </h2>
        <form onSubmit={submit} className="mt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-semibold">
              First name (required)
              <input
                required
                autoFocus
                name="firstName"
                defaultValue={user.name.firstName}
                maxLength={100}
                className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
              />
            </label>
            <label className="text-sm font-semibold">
              Last name (required)
              <input
                required
                name="lastName"
                defaultValue={user.name.lastName}
                maxLength={100}
                className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
              />
            </label>
            <label className="text-sm font-semibold sm:col-span-2">
              Status
              <div className="mt-1 font-normal">
                <SearchableSelect
                  value={status}
                  onChange={setStatus}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'suspended', label: 'Suspended' },
                    { value: 'disabled', label: 'Disabled' },
                  ]}
                />
              </div>
            </label>
          </div>
          {pendingValues && (
            <div className="mt-3 rounded-control border border-red-200 bg-red-50 p-2.5">
              <p className="text-sm text-red-900">
                Set {user.name.firstName} {user.name.lastName}'s status to "{status}"? This
                immediately signs them out and prevents sign-in. They'll stay assigned to any
                applicants already assigned to them.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving}
                  className="schd-button-destructive-strong min-h-9"
                  onClick={() => void applyUpdate(pendingValues)}
                >
                  {saving ? 'Saving…' : 'Confirm status change'}
                </button>
                <button
                  type="button"
                  className="min-h-9 px-2 text-sm underline"
                  onClick={() => setPendingValues(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Cancel
            </button>
            <button disabled={saving} className="schd-button-primary">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NoticeEditor({
  notice,
  workflowStageId,
  onSaved,
  onCancel,
  onDeleted,
}: {
  notice?: Notice;
  workflowStageId: string;
  onSaved: () => void;
  onCancel?: () => void;
  onDeleted?: () => void;
}) {
  const [editing, setEditing] = useState(!notice);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setSaving(true);
    try {
      const body = {
        workflowStageId,
        published: values.get('published') === 'on',
        translations: {
          en: { title: values.get('titleEn'), body: values.get('bodyEn') },
          es: { title: values.get('titleEs'), body: values.get('bodyEs') },
        },
      };
      if (notice) {
        await api(`/settings/notices/${notice.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        toast.success('Notice updated.');
        setEditing(false);
      } else {
        await api('/settings/notices', { method: 'POST', body: JSON.stringify(body) });
        toast.success('Notice created.');
      }
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const deleteNotice = async () => {
    setDeleting(true);
    try {
      await api(`/settings/notices/${notice!.id}`, { method: 'DELETE' });
      toast.success('Notice deleted.');
      onDeleted?.();
    } catch (error) {
      toast.error(errorMessage(error));
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };
  if (notice && !editing)
    return (
      <article className="rounded-control border border-border bg-surface p-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">
              {notice.translations.en.title}
              <StatusBadge tone={versionPublishedTone(notice.published)}>
                {notice.published ? 'Published' : 'Draft'}
              </StatusBadge>
            </p>
            <p className="text-xs text-ink/55">ES: {notice.translations.es.title}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              className="min-h-8 rounded-control border px-2 text-xs"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
            <button
              className="min-h-8 rounded-control border border-red-300 px-2 text-xs text-red-800"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </button>
          </div>
        </div>
        {confirmingDelete && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2 rounded-control border border-red-200 bg-red-50 p-2.5 text-sm text-red-900">
            <span>
              Delete this notice? Applicants in this stage will no longer see it. This can't be
              undone.
            </span>
            <div className="flex gap-2">
              <button
                disabled={deleting}
                onClick={() => void deleteNotice()}
                className="min-h-8 rounded-control bg-red-700 px-2 text-xs text-white disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Confirm delete'}
              </button>
              <button
                type="button"
                className="min-h-8 px-2 text-xs underline"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </article>
    );
  return (
    <form
      onSubmit={submit}
      className="rounded-control border border-border bg-surface-muted p-3"
    >
      <h3 className="text-sm font-semibold">{notice ? 'Edit notice' : 'New notice'}</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          Title (English, required)
          <input
            required
            name="titleEn"
            defaultValue={notice?.translations.en.title}
            maxLength={300}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Title (Spanish, required)
          <input
            required
            name="titleEs"
            defaultValue={notice?.translations.es.title}
            maxLength={300}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Body (English, required)
          <textarea
            required
            name="bodyEn"
            defaultValue={notice?.translations.en.body}
            rows={3}
            className="mt-1 block w-full rounded-control border bg-white px-2 py-1"
          />
        </label>
        <label className="text-sm">
          Body (Spanish, required)
          <textarea
            required
            name="bodyEs"
            defaultValue={notice?.translations.es.body}
            rows={3}
            className="mt-1 block w-full rounded-control border bg-white px-2 py-1"
          />
        </label>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm">
        <input type="checkbox" name="published" defaultChecked={notice?.published ?? false} />
        Published — visible to applicants currently in this stage
      </label>
      <div className="mt-3 flex gap-2">
        <button
          disabled={saving}
          className="min-h-9 rounded-control bg-burgundy px-3 text-sm text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : notice ? 'Save notice' : 'Create notice'}
        </button>
        <button
          type="button"
          className="min-h-9 px-2 text-sm underline"
          onClick={() => (notice ? setEditing(false) : onCancel?.())}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function StudyMaterialsPanel() {
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [languageFilter, setLanguageFilter] = useState('all');
  const [query, setQuery] = useState('');
  const materials = useQuery({
    queryKey: ['settings', 'materials'],
    queryFn: () => api<{ data: StudyMaterial[] }>('/settings/study-materials'),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['settings', 'materials'] });
  const all = materials.data?.data ?? [];
  const normalizedQuery = query.trim().toLowerCase();
  const visible = all.filter((m) => {
    if (languageFilter !== 'all' && m.language !== languageFilter) return false;
    if (!normalizedQuery) return true;
    return (
      m.title.toLowerCase().includes(normalizedQuery) ||
      (m.description ?? '').toLowerCase().includes(normalizedQuery) ||
      m.fileName.toLowerCase().includes(normalizedQuery)
    );
  });
  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="size-5 text-burgundy" aria-hidden="true" />
          <h1 className="font-display text-2xl sm:text-3xl">Shared Resources ({all.length})</h1>
          <InfoTip label="About shared resources">
            PDFs uploaded here form a library staff can assign to any applicant from their
            Shared resources tab — uploading one here doesn't share it with anyone until it's
            assigned. If a resource is outdated, delete it and upload the replacement — there's
            no separate edit step.
          </InfoTip>
        </div>
        <button className="schd-button-primary" onClick={() => setUploadOpen(true)}>
          <FileUp className="size-5" /> Upload resource
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="w-full max-w-[220px]">
          <SearchableSelect
            value={languageFilter}
            onChange={setLanguageFilter}
            options={[
              { value: 'all', label: 'All languages' },
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Español' },
            ]}
          />
        </div>
        <label className="relative w-full sm:w-72">
          <span className="sr-only">Search resources</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink/45"
            aria-hidden="true"
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search resources…"
            className="min-h-10 w-full rounded-control border bg-white pl-9 pr-3 text-sm"
          />
        </label>
      </div>
      <div className="mt-5">
        {materials.isLoading ? (
          <div className="space-y-3" role="status">
            <span className="sr-only">Loading shared resources</span>
            <div className="schd-skeleton h-5 w-2/5" aria-hidden="true" />
            <div className="schd-skeleton h-12 w-full" aria-hidden="true" />
            <div className="schd-skeleton h-12 w-full" aria-hidden="true" />
          </div>
        ) : materials.isError ? (
          <ErrorRetry query={materials} />
        ) : all.length === 0 ? (
          <p className="rounded-card border border-dashed border-border bg-surface p-6 text-center text-sm text-ink/65">
            No shared resources uploaded yet.
          </p>
        ) : visible.length === 0 ? (
          <p className="rounded-card border border-dashed border-border bg-surface p-6 text-center text-sm text-ink/65">
            No resources match your filters.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((m) => (
              <StudyMaterialCard key={m.id} material={m} onDeleted={invalidate} />
            ))}
          </div>
        )}
      </div>
      {uploadOpen && (
        <StudyMaterialUploadModal
          onCancel={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function PdfDropzone({
  file,
  onFileSelected,
}: {
  file: File | null;
  onFileSelected: (file: File) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFiles = (files: FileList | null) => {
    const picked = files?.[0];
    if (!picked) return;
    if (picked.type !== 'application/pdf') {
      toast.error('Only PDF files can be uploaded here.');
      return;
    }
    onFileSelected(picked);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        handleFiles(event.dataTransfer.files);
      }}
      className={`flex cursor-pointer flex-col items-center gap-2 rounded-control border-2 border-dashed p-6 text-center transition-colors ${
        dragging ? 'border-burgundy bg-burgundy/5' : 'border-border hover:border-burgundy/50'
      }`}
    >
      <FileUp className="size-6 text-ink/40" aria-hidden="true" />
      {file ? (
        <p className="text-sm font-semibold text-ink">{file.name}</p>
      ) : (
        <p className="text-sm text-ink/70">
          Drag and drop your PDF here, or{' '}
          <span className="font-semibold text-burgundy">browse files</span>
        </p>
      )}
      <p className="text-xs text-ink/55">PDF only · max 10 MB</p>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="sr-only"
        onChange={(event) => handleFiles(event.target.files)}
      />
    </div>
  );
}

function StudyMaterialUploadModal({
  onCancel,
  onUploaded,
}: {
  onCancel: () => void;
  onUploaded: () => void;
}) {
  const { closing, requestClose } = useModalClosing(onCancel);
  const dialogRef = useDialogFocus(requestClose);
  const [uploading, setUploading] = useState(false);
  const [language, setLanguage] = useState('en');
  const [file, setFile] = useState<File | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    if (!file) {
      toast.error('Choose a PDF file to upload.');
      return;
    }
    values.set('language', language);
    values.set('file', file);
    setUploading(true);
    try {
      await api('/settings/study-materials', { method: 'POST', body: values });
      toast.success('Shared resource uploaded.');
      onUploaded();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div
      className={`${closing ? 'schd-modal-backdrop-out' : 'schd-modal-backdrop'} fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 sm:grid sm:place-items-center sm:p-4`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-material-title"
        className={`${closing ? 'schd-modal-panel-out' : 'schd-modal-panel'} schd-card max-h-[90vh] w-full max-w-lg overflow-y-auto`}
      >
        <h2 id="upload-material-title" className="font-display text-xl">
          Upload shared resource
        </h2>
        <form onSubmit={submit} className="mt-4">
          <label className="block text-sm font-semibold">
            Title (required)
            <input
              required
              autoFocus
              name="title"
              maxLength={300}
              className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2 font-normal"
            />
          </label>
          <label className="mt-4 block text-sm font-semibold">
            Description (optional)
            <textarea
              name="description"
              rows={2}
              placeholder="Optional summary…"
              className="mt-1 block w-full rounded-control border bg-white px-2 py-1 font-normal"
            />
          </label>
          <label className="mt-4 block text-sm font-semibold">
            Language (required)
            <div className="mt-1 font-normal">
              <SearchableSelect
                value={language}
                onChange={setLanguage}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'es', label: 'Español' },
                ]}
              />
            </div>
          </label>
          <div className="mt-4">
            <span className="block text-sm font-semibold">PDF file (required)</span>
            <div className="mt-1">
              <PdfDropzone file={file} onFileSelected={setFile} />
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="schd-button-secondary" onClick={requestClose}>
              Cancel
            </button>
            <button disabled={uploading} className="schd-button-primary">
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StudyMaterialCard({
  material,
  onDeleted,
}: {
  material: StudyMaterial;
  onDeleted: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteMaterial = async () => {
    setDeleting(true);
    try {
      await api(`/settings/study-materials/${material.id}`, { method: 'DELETE' });
      toast.success('Shared resource deleted.');
      onDeleted();
    } catch (error) {
      toast.error(errorMessage(error));
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };
  return (
    <article className="flex flex-col overflow-hidden rounded-card border border-border bg-surface">
      <div className="flex items-center justify-between gap-3 p-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-control bg-surface-muted text-ink/50">
          <FileText className="size-5" aria-hidden="true" />
        </span>
        <span className="text-xs font-semibold text-ink/55">
          {material.language === 'es' ? 'Español' : 'English'}
        </span>
      </div>
      <div className="min-w-0 flex-1 px-4 pb-4">
        <h3 className="font-semibold break-words">{material.title}</h3>
        {material.description && (
          <p className="mt-1 line-clamp-2 text-sm text-ink/65">{material.description}</p>
        )}
      </div>
      <p className="truncate border-t border-border px-4 py-3 text-sm text-ink/55">
        {material.fileName} · {(material.contentLength / 1_000_000).toFixed(1)} MB
      </p>
      <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
        <a
          href={`${apiBaseUrl}/settings/study-materials/${material.id}/download`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-burgundy hover:underline"
        >
          <Download className="size-4" aria-hidden="true" /> Download
        </a>
        <ActionMenu
          label="More actions"
          items={[
            {
              label: 'Delete',
              destructive: true,
              disabled: deleting,
              onClick: () => setConfirmingDelete(true),
            },
          ]}
        />
      </div>
      {confirmingDelete && (
        <div className="border-t border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-900">
            Delete "{material.title}"? Staff will no longer be able to assign it to applicants.
            Copies already assigned to an applicant's Shared resources are not affected.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              disabled={deleting}
              onClick={() => void deleteMaterial()}
              className="schd-button-destructive-strong min-h-9"
            >
              {deleting ? 'Deleting…' : 'Confirm delete'}
            </button>
            <button
              type="button"
              className="min-h-9 px-2 text-sm underline"
              onClick={() => setConfirmingDelete(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </article>
  );
}


function OutlookConnectionForm({
  connection,
  onSaved,
}: {
  connection: ReturnType<typeof useQuery<OutlookConnection>>;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const data = connection.data;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const clientSecret = String(values.get('clientSecret') || '');
    setSaving(true);
    try {
      await api('/settings/integrations/outlook', {
        method: 'PUT',
        body: JSON.stringify({
          tenantId: values.get('tenantId'),
          clientId: values.get('clientId'),
          ...(clientSecret ? { clientSecret } : {}),
          replyTo: values.get('replyTo') || null,
        }),
      });
      toast.success('Outlook configuration saved. Connect with Microsoft to finish.');
      setEditing(false);
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await api('/settings/integrations/outlook/disconnect', { method: 'POST' });
      toast.success('Outlook disconnected.');
      setConfirmDisconnect(false);
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setDisconnecting(false);
    }
  };
  if (connection.isLoading)
    return (
      <tr>
        <td colSpan={4} className="px-4 py-4" role="status">
          Loading Outlook connection…
        </td>
      </tr>
    );
  if (connection.isError)
    return (
      <tr>
        <td colSpan={4} className="px-4 py-4">
          <ErrorRetry query={connection} />
        </td>
      </tr>
    );
  if (editing)
    return (
      <tr>
        <td colSpan={4} className="p-0">
          <form onSubmit={submit} className="schd-row-reveal border-l-4 border-l-burgundy bg-surface-muted p-4">
            <h3 className="font-semibold">Outlook connection</h3>
            <p className="mt-1 text-xs text-ink/60">
              Create an Azure app registration (Web platform) and enter its details below. Once
              saved, use "Connect with Microsoft" to sign in and grant access.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                Tenant ID (required)
                <input
                  required
                  name="tenantId"
                  defaultValue={data?.tenantId ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                Client (application) ID (required)
                <input
                  required
                  name="clientId"
                  defaultValue={data?.clientId ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                Client secret{' '}
                {data?.clientSecretConfigured ? '(leave blank to keep current)' : '(required)'}
                <input
                  name="clientSecret"
                  type="password"
                  required={!data?.clientSecretConfigured}
                  autoComplete="off"
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm sm:col-span-2">
                Reply-to address (optional)
                <input
                  name="replyTo"
                  type="email"
                  defaultValue={data?.replyTo ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-ink/60">
              The client secret is encrypted at rest and never displayed again after saving.
              Changing the tenant or client ID revokes the current connection — reconnect with
              Microsoft afterward.
            </p>
            <div className="mt-3 flex gap-2">
              <button disabled={saving} className="schd-button-primary">
                {saving ? 'Saving…' : 'Save configuration'}
              </button>
              <button type="button" className="schd-button-secondary" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </form>
        </td>
      </tr>
    );
  return (
    <>
      <tr className="align-top">
        <td className="px-4 py-4">
          <div className="flex items-center gap-2">
            <Mail className="size-5 shrink-0 text-burgundy" aria-hidden="true" />
            <div>
              <p className="font-semibold">Microsoft Outlook</p>
              <p className="text-xs text-ink/55">Email &amp; calendar</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-4">
          {data?.senderMailbox ? (
            <p className="font-medium">{data.senderMailbox}</p>
          ) : (
            <p className="text-ink/55">Not configured</p>
          )}
        </td>
        <td className="px-4 py-4">
          <StatusBadge tone={integrationStatusTone(data?.status ?? 'disconnected')}>
            {data?.status ?? 'disconnected'}
          </StatusBadge>
          {data?.lastErrorCode && <p className="mt-1 text-xs text-ink/55">{data.lastErrorCode}</p>}
        </td>
        <td className="px-4 py-4 text-right">
          <ActionMenu
            items={[
              ...(data?.tenantId && data?.clientId && data?.clientSecretConfigured
                ? [
                    {
                      label: data.status === 'connected' ? 'Reconnect' : 'Connect with Microsoft',
                      href: `${apiBaseUrl}/settings/integrations/outlook/authorize`,
                    },
                  ]
                : []),
              {
                label: data?.configured ? 'Configure' : 'Set up connection',
                onClick: () => setEditing(true),
              },
              ...(data?.configured
                ? [
                    {
                      label: 'Disconnect',
                      destructive: true,
                      disabled: disconnecting,
                      onClick: () => setConfirmDisconnect(true),
                    },
                  ]
                : []),
            ]}
          />
        </td>
      </tr>
      {confirmDisconnect && (
        <tr>
          <td colSpan={4} className="p-0">
            <div className="border-l-4 border-l-red-400 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-900">Disconnect Microsoft Outlook?</p>
              <p className="mt-1 text-sm text-red-900">
                Email functionality that depends on this connection may stop working until Outlook
                is connected again.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={disconnecting}
                  className="schd-button-destructive-strong"
                  onClick={() => void disconnect()}
                >
                  {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
                <button
                  type="button"
                  className="min-h-11 px-2 text-sm underline"
                  onClick={() => setConfirmDisconnect(false)}
                >
                  Keep connected
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ClickSendConnectionForm({
  connection,
  onSaved,
}: {
  connection: ReturnType<typeof useQuery<ClickSendConnection>>;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const data = connection.data;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const apiKey = String(values.get('apiKey') || '');
    setSaving(true);
    try {
      await api('/settings/integrations/clicksend', {
        method: 'PUT',
        body: JSON.stringify({
          username: values.get('username'),
          ...(apiKey ? { apiKey } : {}),
          senderId: values.get('senderId'),
          defaultCountry: values.get('defaultCountry') || null,
          enabled: values.get('enabled') === 'on',
        }),
      });
      toast.success('ClickSend connection saved.');
      setEditing(false);
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  if (connection.isLoading)
    return (
      <tr>
        <td colSpan={4} className="px-4 py-4" role="status">
          Loading ClickSend connection…
        </td>
      </tr>
    );
  if (connection.isError)
    return (
      <tr>
        <td colSpan={4} className="px-4 py-4">
          <ErrorRetry query={connection} />
        </td>
      </tr>
    );
  if (editing)
    return (
      <tr>
        <td colSpan={4} className="p-0">
          <form onSubmit={submit} className="schd-row-reveal border-l-4 border-l-burgundy bg-surface-muted p-4">
            <h3 className="font-semibold">ClickSend connection</h3>
            <p className="mt-1 text-xs text-ink/60">
              Enter the API credentials from your ClickSend dashboard (Account &gt; API
              Credentials).
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="mt-1 flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" name="enabled" defaultChecked={data?.status !== 'disabled'} />
                Enabled
              </label>
              <label className="text-sm">
                Username (required)
                <input
                  required
                  name="username"
                  defaultValue={data?.username ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                API key {data?.configured ? '(leave blank to keep current)' : '(required)'}
                <input
                  name="apiKey"
                  type="password"
                  required={!data?.configured}
                  autoComplete="off"
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                Sender ID (required)
                <input
                  required
                  name="senderId"
                  maxLength={11}
                  placeholder="e.g. SCHD"
                  defaultValue={data?.senderId ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                Default country code (optional)
                <input
                  name="defaultCountry"
                  maxLength={2}
                  placeholder="e.g. US"
                  defaultValue={data?.defaultCountry ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-ink/60">
              The API key is encrypted at rest and never displayed again after saving. Sender ID
              is alphanumeric, up to 11 characters, and appears as the "from" name on delivered
              texts where the destination carrier supports it.
            </p>
            <div className="mt-3 flex gap-2">
              <button disabled={saving} className="schd-button-primary">
                {saving ? 'Saving…' : 'Save connection'}
              </button>
              <button type="button" className="schd-button-secondary" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </form>
        </td>
      </tr>
    );
  return (
    <tr className="align-top">
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="size-5 shrink-0 text-burgundy" aria-hidden="true" />
          <div>
            <p className="font-semibold">ClickSend</p>
            <p className="text-xs text-ink/55">SMS provider</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        {data?.username ? (
          <>
            <p className="font-medium">{data.username}</p>
            {data?.senderId && <p className="text-xs text-ink/55">Sender ID: "{data.senderId}"</p>}
          </>
        ) : (
          <p className="text-ink/55">Not configured</p>
        )}
      </td>
      <td className="px-4 py-4">
        <StatusBadge tone={integrationStatusTone(data?.status ?? 'disconnected')}>
          {data?.status ?? 'disconnected'}
        </StatusBadge>
        {data?.lastErrorCode && <p className="mt-1 text-xs text-ink/55">{data.lastErrorCode}</p>}
      </td>
      <td className="px-4 py-4 text-right">
        <ActionMenu
          items={[
            {
              label: data?.configured ? 'Configure' : 'Set up connection',
              onClick: () => setEditing(true),
            },
          ]}
        />
      </td>
    </tr>
  );
}

function CalcomConnectionForm({
  connection,
  onSaved,
}: {
  connection: ReturnType<typeof useQuery<CalcomConnection>>;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const data = connection.data;
  const [presentation, setPresentation] = useState<string>(data?.presentation ?? 'embed');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const apiCredential = String(values.get('apiCredential') || '');
    const webhookSecret = String(values.get('webhookSecret') || '');
    setSaving(true);
    try {
      await api('/settings/integrations/calcom', {
        method: 'PUT',
        body: JSON.stringify({
          enabled: values.get('enabled') === 'on',
          portalBaseUrl: values.get('portalBaseUrl'),
          presentation,
          organizationId: values.get('organizationId') || null,
          teamId: values.get('teamId') || null,
          ...(apiCredential ? { apiCredential } : {}),
          ...(webhookSecret ? { webhookSecret } : {}),
        }),
      });
      toast.success('Cal.com connection saved.');
      setEditing(false);
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  if (connection.isLoading)
    return (
      <tr>
        <td colSpan={4} className="px-4 py-4" role="status">
          Loading Cal.com connection…
        </td>
      </tr>
    );
  if (connection.isError)
    return (
      <tr>
        <td colSpan={4} className="px-4 py-4">
          <ErrorRetry query={connection} />
        </td>
      </tr>
    );
  if (editing)
    return (
      <tr>
        <td colSpan={4} className="p-0">
          <form onSubmit={submit} className="schd-row-reveal border-l-4 border-l-burgundy bg-surface-muted p-4">
            <h3 className="font-semibold">Cal.com connection</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="mt-1 flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" name="enabled" defaultChecked={data?.status !== 'disabled'} />
                Enabled
              </label>
              <label className="text-sm sm:col-span-2">
                Portal base URL (required)
                <input
                  required
                  name="portalBaseUrl"
                  type="url"
                  placeholder="https://cal.com/your-district"
                  defaultValue={data?.portalBaseUrl ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                API credential {data?.configured ? '(leave blank to keep current)' : '(required)'}
                <input
                  name="apiCredential"
                  type="password"
                  required={!data?.configured}
                  autoComplete="off"
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                Webhook secret {data?.configured ? '(leave blank to keep current)' : '(recommended)'}
                <input
                  name="webhookSecret"
                  type="password"
                  autoComplete="off"
                  minLength={16}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                Organization ID (optional)
                <input
                  name="organizationId"
                  defaultValue={data?.organizationId ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                Team ID (optional)
                <input
                  name="teamId"
                  defaultValue={data?.teamId ?? ''}
                  className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                />
              </label>
              <label className="text-sm">
                Portal presentation
                <div className="mt-1">
                  <SearchableSelect
                    value={presentation}
                    onChange={setPresentation}
                    options={[
                      { value: 'embed', label: 'Embedded scheduler' },
                      { value: 'redirect', label: 'Redirect to Cal.com' },
                    ]}
                  />
                </div>
              </label>
            </div>
            <p className="mt-2 text-xs text-ink/60">
              Credentials are encrypted at rest and never displayed again after saving. Use a
              Cal.com API key/OAuth credential scoped to this district's organization or team —
              never the Cal.com sign-in email or password.
            </p>
            <div className="mt-3 flex gap-2">
              <button disabled={saving} className="schd-button-primary">
                {saving ? 'Saving…' : 'Save connection'}
              </button>
              <button type="button" className="schd-button-secondary" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </form>
        </td>
      </tr>
    );
  return (
    <tr className="align-top">
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="size-5 shrink-0 text-burgundy" aria-hidden="true" />
          <div>
            <p className="font-semibold">Cal.com</p>
            <p className="text-xs text-ink/55">Scheduling</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        {data?.portalBaseUrl ? (
          <>
            <p className="font-medium break-all">{data.portalBaseUrl}</p>
            {(data?.organizationId || data?.teamId) && (
              <p className="text-xs text-ink/55">
                {data.teamId ? `Team: ${data.teamId}` : `Org: ${data.organizationId}`}
              </p>
            )}
          </>
        ) : (
          <p className="text-ink/55">Not configured</p>
        )}
      </td>
      <td className="px-4 py-4">
        <StatusBadge tone={integrationStatusTone(data?.status ?? 'disconnected')}>
          {data?.status ?? 'disconnected'}
        </StatusBadge>
        {data?.lastErrorCode && <p className="mt-1 text-xs text-ink/55">{data.lastErrorCode}</p>}
      </td>
      <td className="px-4 py-4 text-right">
        <ActionMenu
          items={[
            {
              label: data?.configured ? 'Configure' : 'Connect Cal.com',
              onClick: () => {
                setPresentation(data?.presentation ?? 'embed');
                setEditing(true);
              },
            },
          ]}
        />
      </td>
    </tr>
  );
}

function AppointmentTypeForm({
  stages,
  appointmentType,
  onCancel,
  onSaved,
}: {
  stages: WorkflowStage[];
  appointmentType?: AppointmentType;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState<string>(appointmentType?.type ?? 'interview');
  const [advanceStageId, setAdvanceStageId] = useState(
    appointmentType?.advanceToWorkflowStageId ?? '',
  );
  const editing = Boolean(appointmentType);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const eligibleStageIds = stages
      .filter((s) => values.get(`stage-${s.id}`) === 'on')
      .map((s) => s.id);
    if (!eligibleStageIds.length) {
      toast.error('Select at least one eligible workflow stage.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        type,
        eventTypeId: values.get('eventTypeId'),
        eventTypeSlug: values.get('eventTypeSlug'),
        name: { en: values.get('nameEn'), es: values.get('nameEs') },
        description: {
          en: values.get('descriptionEn') || null,
          es: values.get('descriptionEs') || null,
        },
        durationMinutes: values.get('durationMinutes')
          ? Number(values.get('durationMinutes'))
          : null,
        eligibleStageIds,
        selfServiceCancel: values.get('selfServiceCancel') === 'on',
        selfServiceReschedule: values.get('selfServiceReschedule') === 'on',
        advanceToWorkflowStageId: advanceStageId || null,
        active: values.get('active') === 'on',
      };
      if (editing) {
        await api(`/settings/appointment-types/${appointmentType!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        toast.success('Appointment type updated.');
      } else {
        await api('/settings/appointment-types', { method: 'POST', body: JSON.stringify(body) });
        toast.success('Appointment type created.');
      }
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="schd-row-reveal border-l-4 border-l-burgundy bg-surface-muted p-4">
      <h3 className="font-semibold">
        {editing ? `Edit ${appointmentType!.name.en}` : 'New appointment type'}
      </h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          Type
          <div className="mt-1">
            <SearchableSelect
              value={type ?? 'interview'}
              onChange={setType}
              options={[
                { value: 'interview', label: 'Interview' },
                { value: 'exam', label: 'Exam' },
              ]}
            />
          </div>
        </label>
        <label className="text-sm">
          Duration (minutes, optional)
          <input
            name="durationMinutes"
            type="number"
            min={1}
            defaultValue={appointmentType?.durationMinutes ?? undefined}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Cal.com event type ID (required)
          <input
            required
            name="eventTypeId"
            defaultValue={appointmentType?.eventTypeId}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Cal.com event type slug (required)
          <input
            required
            name="eventTypeSlug"
            defaultValue={appointmentType?.eventTypeSlug ?? undefined}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Name (English, required)
          <input
            required
            name="nameEn"
            maxLength={200}
            defaultValue={appointmentType?.name.en}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Name (Spanish, required)
          <input
            required
            name="nameEs"
            maxLength={200}
            defaultValue={appointmentType?.name.es}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Description (English, optional)
          <input
            name="descriptionEn"
            maxLength={1000}
            defaultValue={appointmentType?.description.en ?? undefined}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Description (Spanish, optional)
          <input
            name="descriptionEs"
            maxLength={1000}
            defaultValue={appointmentType?.description.es ?? undefined}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          Advance to stage on booking (optional)
          <div className="mt-1">
            <SearchableSelect
              value={advanceStageId}
              onChange={setAdvanceStageId}
              placeholder="Don't change the stage automatically"
              options={[
                { value: '', label: "Don't change the stage automatically" },
                ...stages.map((stage) => ({
                  value: stage.id,
                  label: stage.translations.en.name,
                })),
              ]}
            />
          </div>
          <span className="mt-1 block text-xs text-ink/60">
            When set, confirming a booking of this type automatically moves the application to
            this workflow stage.
          </span>
        </label>
        <label className="mt-1 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            name="selfServiceCancel"
            defaultChecked={appointmentType?.selfServiceCancel ?? true}
          />
          Applicants may self-cancel
        </label>
        <label className="mt-1 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            name="selfServiceReschedule"
            defaultChecked={appointmentType?.selfServiceReschedule ?? true}
          />
          Applicants may self-reschedule
        </label>
        <label className="mt-1 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            name="active"
            defaultChecked={appointmentType?.active ?? true}
          />
          Active
        </label>
      </div>
      <fieldset className="mt-3">
        <legend className="text-sm font-semibold">Eligible workflow stages (required)</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {stages.map((stage) => (
            <label key={stage.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name={`stage-${stage.id}`}
                defaultChecked={appointmentType?.eligibleStageIds.includes(stage.id) ?? false}
              />
              {stage.translations.en.name}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="mt-3 flex gap-2">
        <button
          disabled={saving}
          className="schd-button-primary"
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Create appointment type'}
        </button>
        <button type="button" className="schd-button-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const NOTIFICATION_LABELS: Record<string, string> = {
  applicant_invited: 'Applicant invited',
  invite_resent: 'Invitation resent',
  password_reset_requested: 'Password reset requested',
  stage_changed: 'Application status changed',
  document_request_created: 'Document requested',
  document_request_status_changed: 'Document status changed',
  document_submitted: 'Document submitted',
};
const NOTIFICATION_VARIABLES: Record<string, string[]> = {
  applicant_invited: ['applicantName', 'inviteLink', 'expiresInHours'],
  invite_resent: ['applicantName', 'inviteLink', 'expiresInHours'],
  password_reset_requested: ['applicantName', 'resetLink', 'expiresInMinutes'],
  stage_changed: ['applicantName', 'currentStatus', 'portalUrl'],
  document_request_created: ['applicantName', 'documentName', 'portalUrl'],
  document_request_status_changed: ['applicantName', 'documentName', 'requestStatus', 'portalUrl'],
  document_submitted: ['applicantName', 'documentName', 'portalUrl'],
};
// SMS is only offered for admin -> applicant events, and reuses the same label/variables as its
// email counterpart -- each of these codes has a matching `<code>_sms` template (see migration
// 0008_sms_notifications.sql).
for (const code of [
  'applicant_invited',
  'invite_resent',
  'stage_changed',
  'document_request_created',
  'document_request_status_changed',
]) {
  NOTIFICATION_LABELS[`${code}_sms`] = NOTIFICATION_LABELS[code]!;
  NOTIFICATION_VARIABLES[`${code}_sms`] = NOTIFICATION_VARIABLES[code]!;
}
const DIRECTION_LABEL: Record<NotificationTemplate['direction'], string> = {
  admin_to_applicant: 'Applicant',
  applicant_to_admin: 'Admin',
};
const DIRECTION_DESCRIPTION: Record<NotificationTemplate['direction'], string> = {
  admin_to_applicant: 'Automatic messages sent to applicants when staff take action.',
  applicant_to_admin: 'Automatic messages sent to staff when an applicant takes action.',
};

function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full outline-offset-2 transition-colors duration-200 ease-in-out disabled:opacity-50 ${
        checked ? 'bg-burgundy hover:bg-burgundy-dark' : 'bg-black/15 hover:bg-black/25'
      }`}
    >
      <span
        className={`inline-block size-5 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
        aria-hidden="true"
      />
    </button>
  );
}

function NotificationActiveToggle({
  template,
  onSaved,
}: {
  template: NotificationTemplate;
  onSaved: () => void;
}) {
  const [pending, setPending] = useState(false);
  const toggle = async (next: boolean) => {
    setPending(true);
    try {
      await api(`/settings/notification-templates/${template.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: next }),
      });
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setPending(false);
    }
  };
  return (
    <Toggle
      checked={template.active}
      onChange={(next) => void toggle(next)}
      disabled={pending}
      label={`${template.active ? 'Disable' : 'Enable'} ${NOTIFICATION_LABELS[template.code] ?? template.code}`}
    />
  );
}

type NotificationEvent = {
  code: string;
  direction: NotificationTemplate['direction'];
  label: string;
  description: string | null;
  email: NotificationTemplate | null;
  sms: NotificationTemplate | null;
};

function groupNotificationEvents(templates: NotificationTemplate[]): NotificationEvent[] {
  const events: NotificationEvent[] = [];
  const index = new Map<string, NotificationEvent>();
  for (const template of templates) {
    const baseCode =
      template.channel === 'sms' && template.code.endsWith('_sms')
        ? template.code.slice(0, -'_sms'.length)
        : template.code;
    const key = `${template.direction}:${baseCode}`;
    let event = index.get(key);
    if (!event) {
      event = {
        code: baseCode,
        direction: template.direction,
        label: NOTIFICATION_LABELS[baseCode] ?? baseCode,
        description: null,
        email: null,
        sms: null,
      };
      index.set(key, event);
      events.push(event);
    }
    if (template.channel === 'email') event.email = template;
    else event.sms = template;
    event.description ??= template.description;
  }
  return events;
}

function NotificationChannelStatus({
  icon: Icon,
  label,
  template,
  onSaved,
}: {
  icon: typeof Mail;
  label: string;
  template: NotificationTemplate | null;
  onSaved: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="size-4 shrink-0 text-ink/40" aria-hidden="true" />
      <span className="text-sm text-ink/70">{label}</span>
      {template ? (
        <NotificationActiveToggle template={template} onSaved={onSaved} />
      ) : (
        <span className="text-sm text-ink/35">Not available</span>
      )}
    </span>
  );
}

function NotificationsPanel({
  templates,
  onSaved,
}: {
  templates: NotificationTemplate[];
  onSaved: () => void;
}) {
  const events = groupNotificationEvents(templates);
  const [filter, setFilter] = useState<'all' | NotificationTemplate['direction']>('all');
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<{
    code: string;
    direction: NotificationTemplate['direction'];
  } | null>(null);

  const counts = {
    all: events.length,
    admin_to_applicant: events.filter((e) => e.direction === 'admin_to_applicant').length,
    applicant_to_admin: events.filter((e) => e.direction === 'applicant_to_admin').length,
  };
  const normalizedQuery = query.trim().toLowerCase();
  const visible = events.filter((event) => {
    if (filter !== 'all' && event.direction !== filter) return false;
    if (!normalizedQuery) return true;
    return (
      event.label.toLowerCase().includes(normalizedQuery) ||
      (event.description ?? '').toLowerCase().includes(normalizedQuery)
    );
  });
  const selectedEvent = selectedKey
    ? (events.find((e) => e.code === selectedKey.code && e.direction === selectedKey.direction) ??
      null)
    : null;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <MessageSquare className="size-5 text-burgundy" aria-hidden="true" />
        <h3 className="text-lg font-semibold">Notifications</h3>
        <InfoTip label="About notifications">
          These are the automatic notifications the system sends. Select one to preview or edit
          its subject and body.
        </InfoTip>
      </div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div
          className="flex rounded-control border border-border p-0.5"
          role="group"
          aria-label="Filter by direction"
        >
          {(['all', 'admin_to_applicant', 'applicant_to_admin'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-current={filter === value ? 'true' : undefined}
              onClick={() => setFilter(value)}
              className={`min-h-9 whitespace-nowrap rounded-control px-3 text-sm font-semibold ${
                filter === value ? 'bg-burgundy text-white' : 'text-ink/65 hover:text-ink'
              }`}
            >
              {value === 'all' ? 'All' : DIRECTION_LABEL[value]} ({counts[value]})
            </button>
          ))}
        </div>
        <label className="relative w-full sm:w-72">
          <span className="sr-only">Search notifications</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink/45"
            aria-hidden="true"
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search notifications…"
            className="min-h-10 w-full rounded-control border bg-white pl-9 pr-3 text-sm"
          />
        </label>
      </div>
      <section className="overflow-hidden rounded-card border border-border bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-muted text-xs font-semibold uppercase tracking-wide text-ink/60">
                <th className="w-[60%] px-4 py-3 font-semibold">Event / trigger</th>
                <th className="w-[40%] px-4 py-3 font-semibold">Channels</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-4 py-6 text-center text-sm text-ink/60">
                    {normalizedQuery
                      ? 'No notifications match your search.'
                      : 'No notifications configured yet.'}
                  </td>
                </tr>
              ) : (
                visible.map((event) => (
                  <tr
                    key={`${event.direction}:${event.code}`}
                    tabIndex={0}
                    role="button"
                    aria-label={`View ${event.label}`}
                    onClick={() => setSelectedKey({ code: event.code, direction: event.direction })}
                    onKeyDown={(keyEvent) => {
                      if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                        keyEvent.preventDefault();
                        setSelectedKey({ code: event.code, direction: event.direction });
                      }
                    }}
                    className="schd-row-hover cursor-pointer bg-surface hover:bg-surface-muted"
                  >
                    <td className="px-4 py-4">
                      <p className="font-semibold">{event.label}</p>
                      {event.description && (
                        <p className="mt-0.5 text-sm text-ink/65">{event.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap items-center gap-4">
                        <NotificationChannelStatus
                          icon={Mail}
                          label="Email"
                          template={event.email}
                          onSaved={onSaved}
                        />
                        <NotificationChannelStatus
                          icon={MessageSquare}
                          label="SMS"
                          template={event.sms}
                          onSaved={onSaved}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      {selectedEvent && (
        <NotificationDrawer
          event={selectedEvent}
          onClose={() => setSelectedKey(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function NotificationDrawer({
  event,
  onClose,
  onSaved,
}: {
  event: NotificationEvent;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [channel, setChannel] = useState<'email' | 'sms'>(event.email ? 'email' : 'sms');
  const template = channel === 'email' ? event.email : event.sms;
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const version = template?.versions.find((v) => v.language === language && v.published) ?? null;
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [subject, setSubject] = useState(version?.subject ?? '');
  const [body, setBody] = useState(version?.body ?? '');
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSubject(version?.subject ?? '');
    setBody(version?.body ?? '');
    setMode('preview');
  }, [template?.id, language]);

  useEffect(() => {
    dialogRef.current?.focus();
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!template) return null;

  const dirty = subject !== (version?.subject ?? '') || body !== (version?.body ?? '');
  const isEmail = template.channel === 'email';

  const save = async () => {
    setSaving(true);
    try {
      const created = await api<{ id: string }>(
        `/settings/notification-templates/${template.id}/versions`,
        {
          method: 'POST',
          body: JSON.stringify({ language, subject: isEmail ? subject : null, body }),
        },
      );
      await api(`/settings/notification-templates/${template.id}/versions/${created.id}/publish`, {
        method: 'POST',
      });
      toast.success(`${language === 'en' ? 'English' : 'Spanish'} notification updated.`);
      setMode('preview');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="schd-modal-backdrop fixed inset-0 z-[70] bg-ink/50">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-drawer-title"
        className="schd-drawer-panel flex h-full w-full flex-col bg-surface"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border p-5">
          <div>
            <div className="flex flex-wrap items-center gap-1.5">
              <h3 id="notification-drawer-title" className="text-xl font-semibold">
                {event.label}
              </h3>
              <StatusBadge tone="neutral">{DIRECTION_LABEL[event.direction]}</StatusBadge>
            </div>
            <p className="mt-1 text-sm text-ink/65">{DIRECTION_DESCRIPTION[event.direction]}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-control text-ink/60 hover:bg-surface-muted hover:text-ink"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <div className="mx-auto max-w-4xl">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-ink/70">
                {template.active ? 'On' : 'Off'}
              </span>
              <NotificationActiveToggle template={template} onSaved={onSaved} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {event.email && event.sms && (
                <div className="flex rounded-control border border-border p-0.5">
                  {(['email', 'sms'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setChannel(value)}
                      className={`min-h-9 rounded-control px-3 text-sm font-semibold ${
                        channel === value ? 'bg-burgundy text-white' : 'text-ink/65'
                      }`}
                    >
                      {value === 'email' ? 'Email' : 'SMS'}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex rounded-control border border-border p-0.5">
                {(['en', 'es'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setLanguage(lang)}
                    className={`min-h-9 rounded-control px-3 text-sm font-semibold ${
                      language === lang ? 'bg-burgundy text-white' : 'text-ink/65'
                    }`}
                  >
                    {lang === 'en' ? 'English' : 'Español'}
                  </button>
                ))}
              </div>
              {mode === 'preview' ? (
                <button type="button" className="schd-button-secondary" onClick={() => setMode('edit')}>
                  Edit
                </button>
              ) : (
                <button
                  type="button"
                  className="schd-button-secondary"
                  onClick={() => setMode('preview')}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
          {!version && (
            <p className="mb-3 rounded-control border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
              No published {language === 'en' ? 'English' : 'Spanish'} version yet — this
              notification will not send until one is saved.
            </p>
          )}
          {mode === 'preview' ? (
            isEmail ? (
              <div className="rounded-card border border-border bg-surface">
                <div className="border-b border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">
                    Subject
                  </p>
                  <p className="mt-1 font-semibold">{subject || '(no subject saved)'}</p>
                </div>
                <iframe
                  key={`${template.id}-${language}`}
                  title={`${template.code} ${language} preview`}
                  srcDoc={
                    body ||
                    '<p style="font-family:sans-serif;color:#888;padding:24px;">Nothing saved yet.</p>'
                  }
                  sandbox=""
                  className="h-[50vh] w-full rounded-b-card bg-white"
                />
              </div>
            ) : (
              <div className="rounded-card border border-border bg-surface p-6">
                <div className="mx-auto max-w-sm rounded-2xl bg-burgundy px-4 py-3 text-sm text-white shadow">
                  {body || 'Nothing saved yet.'}
                </div>
                <p className="mt-3 text-center text-xs text-ink/50">
                  {body.length} / 918 characters
                </p>
              </div>
            )
          ) : (
            <div className="rounded-card border border-border bg-surface p-4">
              {isEmail && (
                <label className="block text-sm font-semibold">
                  Subject
                  <input
                    value={subject ?? ''}
                    onChange={(inputEvent) => setSubject(inputEvent.target.value)}
                    maxLength={300}
                    className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
                  />
                </label>
              )}
              <label className={`block text-sm font-semibold ${isEmail ? 'mt-4' : ''}`}>
                {isEmail ? 'HTML / CSS body' : 'Message body'}
                <textarea
                  value={body}
                  onChange={(inputEvent) => setBody(inputEvent.target.value)}
                  rows={isEmail ? 16 : 6}
                  maxLength={isEmail ? 20000 : 918}
                  spellCheck={isEmail ? false : true}
                  className={`mt-1 block w-full rounded-control border bg-white px-3 py-2 leading-relaxed ${
                    isEmail ? 'h-[40vh] font-mono text-xs' : 'text-sm'
                  }`}
                />
              </label>
              {!isEmail && (
                <p className="mt-1 text-xs text-ink/50">{body.length} / 918 characters</p>
              )}
              <p className="mt-2 text-xs text-ink/60">
                Variables used by this notification:{' '}
                {(NOTIFICATION_VARIABLES[template.code] ?? []).map((name, index) => (
                  <span key={name}>
                    {index > 0 ? ', ' : ''}
                    <code>{`{{${name}}}`}</code>
                  </span>
                ))}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={!dirty || saving || (isEmail && !subject.trim()) || !body.trim()}
                  onClick={() => void save()}
                  className="schd-button-primary"
                >
                  {saving ? 'Saving…' : 'Save and publish'}
                </button>
                <button
                  type="button"
                  className="schd-button-secondary"
                  onClick={() => setMode('preview')}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkflowStagesPanel({
  stages,
  notices,
  onStagesChanged,
  onNoticesChanged,
}: {
  stages: WorkflowStage[];
  notices: Notice[];
  onStagesChanged: () => void;
  onNoticesChanged: () => void;
}) {
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [creating, setCreating] = useState(stages.length === 0);
  const selectedStage = stages.find((s) => s.id === selectedStageId) ?? stages[0] ?? null;

  return (
    <div className="grid gap-5 lg:grid-cols-[300px_1fr] lg:items-start">
      <section className="overflow-hidden rounded-card border border-border bg-surface lg:sticky lg:top-4">
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h4 className="text-sm font-semibold text-ink/70">Pipeline stages ({stages.length})</h4>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex min-h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-control border border-burgundy px-2.5 text-xs font-semibold text-burgundy hover:bg-burgundy/5"
          >
            <Plus className="size-3.5" aria-hidden="true" /> Add stage
          </button>
        </div>
        <ol className="divide-y divide-border">
          {stages.map((s, index) => {
            const isSelected = !creating && s.id === selectedStage?.id;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  aria-current={isSelected ? 'true' : undefined}
                  onClick={() => {
                    setSelectedStageId(s.id);
                    setCreating(false);
                  }}
                  className={`flex w-full items-center gap-3 border-l-4 px-4 py-3 text-left ${
                    isSelected
                      ? 'border-l-burgundy bg-surface-muted'
                      : 'border-l-transparent hover:bg-surface-muted'
                  }`}
                >
                  <span className="grid size-7 shrink-0 place-items-center rounded-full bg-burgundy text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">
                      {s.translations.en.name}
                    </span>
                    <span className="block truncate text-xs text-ink/55">
                      {s.translations.es.name}
                    </span>
                  </span>
                  {isSelected && <StatusBadge tone="positive">Active</StatusBadge>}
                </button>
              </li>
            );
          })}
        </ol>
      </section>
      <section className="min-w-0 rounded-card border border-border bg-surface p-5">
        {creating ? (
          <CreateStageForm
            stages={stages}
            onCancel={() => setCreating(stages.length === 0)}
            onCreated={() => {
              setCreating(false);
              onStagesChanged();
            }}
          />
        ) : selectedStage ? (
          <StageEditorPanel
            key={selectedStage.id}
            stage={selectedStage}
            notices={notices.filter((n) => n.workflowStageId === selectedStage.id)}
            onSaved={onStagesChanged}
            onDeleted={() => {
              setSelectedStageId(null);
              onStagesChanged();
            }}
            onNoticesChanged={onNoticesChanged}
          />
        ) : (
          <p className="text-sm text-ink/60">No stages yet. Add one to start building the pipeline.</p>
        )}
      </section>
    </div>
  );
}

function StageEditorPanel({
  stage,
  notices,
  onSaved,
  onDeleted,
  onNoticesChanged,
}: {
  stage: WorkflowStage;
  notices: Notice[];
  onSaved: () => void;
  onDeleted: () => void;
  onNoticesChanged: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addingNotice, setAddingNotice] = useState(false);
  const formId = `stage-basic-details-${stage.id}`;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await api(`/settings/workflow-stages/${stage.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          translations: {
            en: { name: values.get('nameEn') },
            es: { name: values.get('nameEs') },
          },
        }),
      });
      toast.success('Application stage updated.');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const deleteStage = async () => {
    setDeleting(true);
    try {
      await api(`/settings/workflow-stages/${stage.id}`, { method: 'DELETE' });
      toast.success('Application stage deleted.');
      onDeleted();
    } catch (error) {
      toast.error(errorMessage(error));
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div className="min-w-0">
          <h4 className="text-lg font-semibold">{stage.translations.en.name}</h4>
          <p className="text-xs text-ink/55">Stage code: {stage.code}</p>
        </div>
        <button type="submit" form={formId} disabled={saving} className="schd-button-primary shrink-0">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <form id={formId} onSubmit={submit} className="rounded-control border border-border p-4">
        <h5 className="text-sm font-semibold text-ink/70">Basic details</h5>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            Name (English, required)
            <input
              required
              name="nameEn"
              defaultValue={stage.translations.en.name}
              maxLength={200}
              className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
            />
          </label>
          <label className="text-sm">
            Name (Spanish, required)
            <input
              required
              name="nameEs"
              defaultValue={stage.translations.es.name}
              maxLength={200}
              className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
            />
          </label>
        </div>
      </form>
      <section className="mt-4 rounded-control border border-border p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-ink/70">
            <Megaphone className="size-4 text-burgundy" aria-hidden="true" />
            Applicant portal notices
          </span>
          <button
            type="button"
            className="inline-flex min-h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-control border px-2.5 text-xs"
            onClick={() => setAddingNotice(true)}
          >
            <Plus className="size-3.5" aria-hidden="true" /> Add notice
          </button>
        </div>
        {notices.length === 0 && !addingNotice && (
          <p className="mt-1.5 text-xs text-ink/55">
            No notices yet — applicants in this stage see nothing extra.
          </p>
        )}
        <div className="mt-2 space-y-2">
          {notices.map((n) => (
            <NoticeEditor
              key={n.id}
              notice={n}
              workflowStageId={stage.id}
              onSaved={onNoticesChanged}
              onDeleted={onNoticesChanged}
            />
          ))}
          {addingNotice && (
            <NoticeEditor
              workflowStageId={stage.id}
              onCancel={() => setAddingNotice(false)}
              onSaved={() => {
                setAddingNotice(false);
                onNoticesChanged();
              }}
            />
          )}
        </div>
      </section>
      <section className="mt-4 rounded-control border border-red-200 p-4">
        <span className="text-sm font-semibold text-red-800">Danger zone</span>
        {!confirmingDelete ? (
          <div className="mt-2">
            <button
              type="button"
              className="schd-button-destructive-sm"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete this stage
            </button>
          </div>
        ) : (
          <div className="mt-2 rounded-control border border-red-200 bg-red-50 p-2.5">
            <p className="text-sm text-red-900">
              Delete "{stage.translations.en.name}"? This can't be undone, and only works if no
              applicant has ever been in this stage. If any applicant has passed through it,
              deletion will be blocked to preserve their history.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={deleting}
                onClick={() => void deleteStage()}
                className="schd-button-destructive-strong min-h-9"
              >
                {deleting ? 'Deleting…' : 'Confirm delete'}
              </button>
              <button
                type="button"
                className="min-h-9 px-2 text-sm underline"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function CreateStageForm({
  stages,
  onCancel,
  onCreated,
}: {
  stages: WorkflowStage[];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [position, setPosition] = useState('end');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await api('/settings/workflow-stages', {
        method: 'POST',
        body: JSON.stringify({
          translations: {
            en: { name: values.get('nameEn') },
            es: { name: values.get('nameEs') },
          },
          insertAfterStageId: position === 'start' ? null : position === 'end' ? undefined : position,
        }),
      });
      toast.success('Application stage added.');
      onCreated();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit}>
      <h3 className="font-semibold">New application stage</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          Name (English, required)
          <input
            required
            autoFocus
            name="nameEn"
            maxLength={200}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm">
          Name (Spanish, required)
          <input
            required
            name="nameEs"
            maxLength={200}
            className="mt-1 block min-h-11 w-full rounded-control border bg-white px-2"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          Position in the pipeline
          <div className="mt-1">
            <SearchableSelect
              value={position}
              onChange={setPosition}
              options={[
                { value: 'start', label: 'At the beginning' },
                ...stages.map((s) => ({
                  value: s.id,
                  label: `Right after "${s.translations.en.name}"`,
                })),
                { value: 'end', label: 'At the end' },
              ]}
            />
          </div>
        </label>
      </div>
      <div className="mt-3 flex gap-2">
        <button disabled={saving} className="schd-button-primary">
          {saving ? 'Adding…' : 'Add stage'}
        </button>
        {stages.length > 0 && (
          <button type="button" className="schd-button-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

const HISTORY_LABELS: Record<string, string> = {
  created: 'Application created',
  advanced: 'Moved forward to',
  skipped: 'Skipped ahead to',
  backtracked: 'Moved back to',
  lifecycle_changed: 'Status changed to',
  archived: 'Archived',
  reopened: 'Reactivated',
};

const STAGE_STATUS_LABELS: Record<StageStatusValue, string> = {
  pending: 'Pending',
  complete: 'Complete',
  not_applicable: 'Not applicable',
};
const STAGE_STATUS_VALUES: StageStatusValue[] = ['pending', 'complete', 'not_applicable'];

function StatusManager({
  application,
  onSaved,
}: {
  application: Application;
  onSaved: () => void;
}) {
  const [busyStageId, setBusyStageId] = useState<string | null>(null);
  const [customBusy, setCustomBusy] = useState(false);
  const [customForm, setCustomForm] = useState<string | null>(null);
  const [removingCustom, setRemovingCustom] = useState(false);
  const checklist = useQuery({
    queryKey: ['application-stages', application.id],
    queryFn: () => api<StageChecklist>(`/applications/${application.id}/stages`),
  });
  const history = useQuery({
    queryKey: ['application-history', application.id],
    queryFn: () => api<StaffApplicationHistory>(`/applications/${application.id}/history`),
  });
  const items = checklist.data?.data ?? [];
  const complete = checklist.data?.meta.complete ?? false;
  const customStatus = checklist.data?.customStatus ?? null;

  const setStageStatus = async (workflowStageId: string, status: StageStatusValue) => {
    setBusyStageId(workflowStageId);
    try {
      await api(`/applications/${application.id}/stages/${workflowStageId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      toast.success('Stage status updated.');
      void checklist.refetch();
      void history.refetch();
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusyStageId(null);
    }
  };

  const saveCustomStatus = async () => {
    const name = customForm?.trim() ?? '';
    if (!name) {
      toast.error('Enter a status name.');
      return;
    }
    setCustomBusy(true);
    try {
      if (customStatus) {
        await api(`/applications/${application.id}/custom-status`, {
          method: 'PATCH',
          body: JSON.stringify({ name }),
        });
        toast.success('Custom status updated.');
      } else {
        await api(`/applications/${application.id}/custom-status`, {
          method: 'POST',
          body: JSON.stringify({ name, status: 'pending' }),
        });
        toast.success('Custom status added.');
      }
      setCustomForm(null);
      void checklist.refetch();
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCustomBusy(false);
    }
  };

  const setCustomStatusValue = async (status: StageStatusValue) => {
    setCustomBusy(true);
    try {
      await api(`/applications/${application.id}/custom-status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      toast.success('Custom status updated.');
      void checklist.refetch();
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCustomBusy(false);
    }
  };

  const removeCustomStatus = async () => {
    setCustomBusy(true);
    try {
      await api(`/applications/${application.id}/custom-status`, { method: 'DELETE' });
      toast.success('Custom status removed.');
      setRemovingCustom(false);
      void checklist.refetch();
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCustomBusy(false);
    }
  };

  return (
    <div>
      <section className="rounded-xl border-2 border-burgundy/20 bg-surface-muted p-4 sm:p-6">
        <div className="flex items-center gap-2">
          <Target className="size-5 text-burgundy" aria-hidden="true" />
          <h3 className="font-semibold">
            {complete ? 'All steps complete' : `Current step: ${application.currentStage.name}`}
          </h3>
        </div>
        <p className="mt-2 text-sm text-ink/65">
          Mark each step pending, complete, or not applicable -- steps don't have to resolve in
          order, and a step marked not applicable never blocks the ones after it. The application is
          complete once nothing is left pending.
        </p>
        {checklist.isError && (
          <div className="schd-error mt-3 text-sm" role="alert">
            {errorMessage(checklist.error)}{' '}
            <button type="button" className="underline" onClick={() => void checklist.refetch()}>
              Retry
            </button>
          </div>
        )}
        {checklist.isLoading ? (
          <p className="mt-4 text-sm" role="status">
            Loading steps…
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {items.map((item) => {
              const isCurrent = !complete && item.id === application.currentStage.id;
              const busy = busyStageId === item.id;
              return (
                <li
                  key={item.id}
                  className={`flex flex-wrap items-center justify-between gap-3 py-3 ${
                    isCurrent ? 'rounded-control bg-burgundy-soft px-3' : ''
                  }`}
                >
                  <div>
                    <p className={`font-semibold ${isCurrent ? 'text-burgundy' : ''}`}>{item.name}</p>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink/55">
                      <StatusBadge tone={stageStatusTone(item.status)}>
                        {STAGE_STATUS_LABELS[item.status]}
                      </StatusBadge>
                      {item.updatedBy && item.updatedAt && (
                        <span>
                          Updated by {item.updatedBy.name} on{' '}
                          {new Date(item.updatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                  <div
                    className="flex rounded-control border border-border p-0.5"
                    role="group"
                    aria-label={`Status for ${item.name}`}
                  >
                    {STAGE_STATUS_VALUES.map((value) => (
                      <button
                        key={value}
                        type="button"
                        disabled={busy || item.status === value}
                        aria-current={item.status === value ? 'true' : undefined}
                        onClick={() => void setStageStatus(item.id, value)}
                        className={`min-h-9 whitespace-nowrap rounded-control px-3 text-sm font-semibold ${
                          item.status === value
                            ? 'bg-burgundy text-white'
                            : 'text-ink/65 hover:text-ink disabled:opacity-50'
                        }`}
                      >
                        {STAGE_STATUS_LABELS[value]}
                      </button>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!checklist.isLoading && (
          <div className="mt-4 border-t border-border pt-4">
            {customStatus ? (
              <div
                className={`rounded-control p-3 ${
                  removingCustom ? 'border border-red-200 bg-red-50' : 'bg-burgundy-soft/40'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {customForm !== null ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="text"
                        value={customForm}
                        onChange={(e) => setCustomForm(e.target.value)}
                        maxLength={200}
                        className="min-h-9 rounded-control border border-border bg-surface px-3 text-sm"
                        aria-label="Custom status name"
                        autoFocus
                      />
                      <button
                        type="button"
                        disabled={customBusy}
                        onClick={() => void saveCustomStatus()}
                        className="schd-button-primary min-h-9 px-3 text-sm"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="min-h-9 px-2 text-sm underline"
                        onClick={() => setCustomForm(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="font-semibold">{customStatus.name}</p>
                      <p className="mt-1 text-xs text-ink/55">Custom status for this applicant only</p>
                    </div>
                  )}
                  {customForm === null && !removingCustom && (
                    <div className="flex items-center gap-2">
                      <div
                        className="flex rounded-control border border-border p-0.5"
                        role="group"
                        aria-label={`Status for ${customStatus.name}`}
                      >
                        {STAGE_STATUS_VALUES.map((value) => (
                          <button
                            key={value}
                            type="button"
                            disabled={customBusy || customStatus.status === value}
                            aria-current={customStatus.status === value ? 'true' : undefined}
                            onClick={() => void setCustomStatusValue(value)}
                            className={`min-h-9 whitespace-nowrap rounded-control px-3 text-sm font-semibold ${
                              customStatus.status === value
                                ? 'bg-burgundy text-white'
                                : 'text-ink/65 hover:text-ink disabled:opacity-50'
                            }`}
                          >
                            {STAGE_STATUS_LABELS[value]}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="schd-button-secondary-sm"
                        onClick={() => setCustomForm(customStatus.name)}
                        aria-label="Rename custom status"
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="schd-button-destructive-sm"
                        onClick={() => setRemovingCustom(true)}
                        aria-label="Remove custom status"
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
                {removingCustom && (
                  <div className="mt-2">
                    <p className="text-sm text-red-900">
                      Remove the custom status "{customStatus.name}"? This can't be undone.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={customBusy}
                        onClick={() => void removeCustomStatus()}
                        className="schd-button-destructive-strong min-h-9"
                      >
                        {customBusy ? 'Removing…' : 'Confirm remove'}
                      </button>
                      <button
                        type="button"
                        className="min-h-9 px-2 text-sm underline"
                        onClick={() => setRemovingCustom(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : customForm !== null ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={customForm}
                  onChange={(e) => setCustomForm(e.target.value)}
                  maxLength={200}
                  placeholder="Status name"
                  className="min-h-9 rounded-control border border-border bg-surface px-3 text-sm"
                  aria-label="Custom status name"
                  autoFocus
                />
                <button
                  type="button"
                  disabled={customBusy}
                  onClick={() => void saveCustomStatus()}
                  className="schd-button-primary min-h-9 px-3 text-sm"
                >
                  Save
                </button>
                <button
                  type="button"
                  className="min-h-9 px-2 text-sm underline"
                  onClick={() => setCustomForm(null)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" className="schd-button-secondary-sm" onClick={() => setCustomForm('')}>
                <Plus className="size-4" aria-hidden="true" /> Add custom status
              </button>
            )}
          </div>
        )}
      </section>
      <div className="mt-6">
        <div className="mb-3 flex items-center gap-2">
          <History className="size-5 text-burgundy" aria-hidden="true" />
          <h3 className="font-semibold">History</h3>
        </div>
        {history.isLoading ? (
          <p className="text-sm" role="status">
            Loading history…
          </p>
        ) : history.isError ? (
          <div className="schd-error text-sm" role="alert">
            {errorMessage(history.error)}{' '}
            <button className="underline" onClick={() => void history.refetch()}>
              Retry
            </button>
          </div>
        ) : history.data?.data.length ? (
          <div className="relative">
            <div
              className="absolute left-[5px] top-2 bottom-2 w-px bg-border"
              aria-hidden="true"
            />
            <ol className="space-y-6">
              {history.data.data.map((entry) => (
                <li key={entry.id} className="relative pl-7 text-sm">
                  <span
                    className="absolute left-0 top-1 size-[11px] rounded-full border-2 border-surface bg-burgundy"
                    aria-hidden="true"
                  />
                  <p className="font-semibold">
                    {HISTORY_LABELS[entry.transitionKind] ?? entry.transitionKind}
                    {entry.transitionKind !== 'created' &&
                      entry.transitionKind !== 'archived' &&
                      entry.transitionKind !== 'reopened' &&
                      entry.stage && <> {entry.stage.name}</>}
                  </p>
                  {entry.fromStage && entry.stage && entry.transitionKind !== 'created' && (
                    <p className="mt-0.5 text-ink/70">
                      {entry.fromStage.name} → {entry.stage.name}
                    </p>
                  )}
                  {entry.reason && <p className="mt-0.5 text-ink/70">"{entry.reason}"</p>}
                  <p className="mt-0.5 text-ink/55">
                    {entry.changedBy?.name ?? 'System'} ·{' '}
                    {new Date(entry.changedAt).toLocaleString()}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <p className="text-sm text-ink/65">No history recorded yet.</p>
        )}
        {history.data && history.data.meta.total > history.data.data.length && (
          <p className="mt-3 text-xs text-ink/55">
            Showing the {history.data.data.length} most recent of {history.data.meta.total} changes.
          </p>
        )}
      </div>
    </div>
  );
}

function AssignmentManager({
  application,
  onSaved,
}: {
  application: Application;
  onSaved: () => void;
}) {
  const [staffUserId, setStaffUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [unassigningId, setUnassigningId] = useState<string | null>(null);
  const [unassignReason, setUnassignReason] = useState('');
  const [unassignBusy, setUnassignBusy] = useState(false);
  const staffDirectory = useQuery({
    queryKey: ['settings', 'users'],
    queryFn: () => api<{ data: StaffUser[] }>('/settings/users'),
  });
  const activeAssignmentIds = new Set(
    application.assignments.filter((item) => !item.unassignedAt).map((item) => item.assignedStaff.id),
  );
  const assignableStaff = (staffDirectory.data?.data ?? []).filter(
    (u) => u.status === 'active' && u.assignable && !activeAssignmentIds.has(u.id),
  );
  const assign = async (event: FormEvent) => {
    event.preventDefault();
    if (!staffUserId) return;
    setSaving(true);
    try {
      await api(`/applications/${application.id}/assignments`, {
        method: 'POST',
        body: JSON.stringify({ staffUserId, primary: application.assignments.length === 0 }),
      });
      toast.success('Staff assignment added.');
      setStaffUserId('');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const unassign = async (assignmentId: string) => {
    if (!unassignReason.trim()) return;
    setUnassignBusy(true);
    try {
      await api(`/applications/${application.id}/assignments/${assignmentId}/unassign`, {
        method: 'POST',
        body: JSON.stringify({ reason: unassignReason }),
      });
      toast.success('Staff member unassigned.');
      setUnassigningId(null);
      setUnassignReason('');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setUnassignBusy(false);
    }
  };
  const activeAssignments = application.assignments.filter((item) => !item.unassignedAt);
  return (
    <div>
      <div className="flex items-center gap-2">
        <Users className="size-5 text-burgundy" aria-hidden="true" />
        <h3 className="font-semibold">Assigned Staff</h3>
        <InfoTip label="About staff assignments">
          One active assignment is primary; additional staff may be assigned for shared
          responsibility.
        </InfoTip>
      </div>
      <p className="mt-1 text-sm text-ink/65">Staff members managing this application.</p>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink/50">
        Currently assigned ({activeAssignments.length})
      </p>
      <ul className="mt-2 space-y-3">
        {activeAssignments.map((item) => (
          <li key={item.id} className="border-t border-border pt-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-burgundy text-xs font-bold text-white">
                  {initialsFromName(item.assignedStaff.name)}
                </span>
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 font-semibold">
                    {item.assignedStaff.name}
                    <StatusBadge tone={item.primary ? 'positive' : 'neutral'}>
                      {item.primary ? 'Primary' : 'Assigned'}
                    </StatusBadge>
                  </p>
                  <p className="truncate text-sm text-ink/65">{item.assignedStaff.email}</p>
                </div>
              </div>
              <button
                type="button"
                className="schd-button-destructive-sm shrink-0"
                onClick={() => {
                  setUnassigningId(item.id);
                  setUnassignReason('');
                }}
              >
                Remove
              </button>
            </div>
            {unassigningId === item.id && (
              <div className="mt-2 rounded-control border border-red-200 bg-red-50 p-2.5">
                <p className="text-sm text-red-900">
                  Removes {item.assignedStaff.name} from this application's team.
                </p>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <label className="min-w-0 flex-1 text-sm text-red-900">
                    Reason (required)
                    <input
                      required
                      value={unassignReason}
                      onChange={(event) => setUnassignReason(event.target.value)}
                      className="mt-1 block min-h-9 w-full rounded-control border bg-white px-2 font-normal text-ink"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={unassignBusy || !unassignReason.trim()}
                    className="schd-button-destructive-strong min-h-9 disabled:opacity-50"
                    onClick={() => void unassign(item.id)}
                  >
                    {unassignBusy ? 'Removing…' : 'Confirm unassignment'}
                  </button>
                  <button
                    type="button"
                    className="min-h-9 px-2 text-sm underline"
                    onClick={() => setUnassigningId(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      <form onSubmit={assign} className="mt-4 border-t border-border pt-4">
        <label className="block text-sm font-semibold">
          Assign staff
          <div className="mt-1 font-normal">
            <SearchableSelect
              required
              value={staffUserId}
              onChange={setStaffUserId}
              disabled={staffDirectory.isLoading}
              placeholder={staffDirectory.isLoading ? 'Loading staff…' : 'Choose a staff member'}
              options={assignableStaff.map((u) => ({
                value: u.id,
                label: `${u.name.firstName} ${u.name.lastName} · ${u.role}`,
              }))}
            />
          </div>
        </label>
        <button disabled={saving} className="schd-button-secondary mt-3">
          {saving ? 'Assigning…' : 'Assign staff member'}
        </button>
      </form>
    </div>
  );
}

function Notes({
  applicationId,
  note,
  setNote,
  busy,
  setBusy,
}: {
  applicationId: string;
  note: string;
  setNote: (value: string) => void;
  busy: boolean;
  setBusy: (value: boolean) => void;
}) {
  const notes = useQuery({
    queryKey: ['notes', applicationId],
    queryFn: () =>
      api<{
        data: Array<{
          id: string;
          body: string;
          createdAt: string;
          createdBy: { name: string } | null;
        }>;
      }>(`/applications/${applicationId}/notes`),
  });
  const add = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api(`/applications/${applicationId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body: note }),
      });
      setNote('');
      await notes.refetch();
      toast.success('Internal note added.');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="flex items-center gap-2">
        <StickyNote className="size-5 text-burgundy" aria-hidden="true" />
        <h3 className="font-semibold">Internal Notes</h3>
        <StatusBadge tone="neutral">Staff only</StatusBadge>
      </div>
      <p className="mt-1 text-sm text-ink/65">
        Notes are permanent records, visible to staff only, and can't be edited once added.
      </p>
      <div className="mt-4 rounded-control border border-border p-3">
        <label className="block text-sm font-semibold">
          <span className="sr-only">Write an internal note</span>
          <textarea
            required
            placeholder="Write an internal note…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="block min-h-24 w-full rounded-control border bg-white p-2 font-normal"
          />
        </label>
        <div className="mt-2 flex justify-end">
          <button disabled={busy || !note.trim()} className="schd-button-primary" onClick={add}>
            {busy ? 'Adding…' : 'Add note'}
          </button>
        </div>
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink/50">
        Recent notes
      </p>
      <div className="mt-2 space-y-3">
        {notes.isLoading ? (
          'Loading notes…'
        ) : notes.isError ? (
          <div className="schd-error text-sm" role="alert">
            {errorMessage(notes.error)}{' '}
            <button className="underline" onClick={() => void notes.refetch()}>
              Retry
            </button>
          </div>
        ) : notes.data?.data.length ? (
          notes.data.data.map((item) => {
            const authorName = item.createdBy?.name ?? 'Former user';
            return (
              <article key={item.id} className="border-t border-border pt-3">
                <div className="flex items-center gap-2">
                  <span className="grid size-7 shrink-0 place-items-center rounded-full bg-burgundy text-[11px] font-bold text-white">
                    {initialsFromName(authorName)}
                  </span>
                  <p className="text-sm font-semibold">
                    {authorName}
                    <span className="ml-1.5 font-normal text-ink/55">
                      · {new Date(item.createdAt).toLocaleString()}
                    </span>
                  </p>
                </div>
                <p className="mt-1.5 text-sm">{item.body}</p>
              </article>
            );
          })
        ) : (
          <p className="text-sm text-ink/65">No internal notes yet.</p>
        )}
      </div>
    </div>
  );
}
